import React, { useEffect } from 'react';
import Icon from '../../ui/Icon';
import { T } from '../../constants/mockData';
import { usersAPI, groupsAPI, masterAPI, slaAPI, auditAPI, requesterGroupsAPI, priorityKeywordsAPI } from '../../services/api';
import AttendancePage from './AttendancePage';
import EmailTemplateEditor from './EmailTemplateEditor';
import SLAAdmin from './SLAAdmin';
import PauseRequestsAdmin from './PauseRequestsAdmin';
import FAQAdmin from './FAQAdmin';
import ResetAdmin from './ResetAdmin';
import AutomationAdmin from './AutomationAdmin';
import M365UserRegistryAdmin from './M365UserRegistryAdmin';
import M365GroupRegistryAdmin from './M365GroupRegistryAdmin';
import M365UsersAdmin from './M365UsersAdmin';
import M365GroupsAdmin from './M365GroupsAdmin';
import { COMMON_TIMEZONES, listAllTimezones, getAppTimezone, setAppTimezone } from '../../utils/timezone';
import { formatInTz } from '../../utils/timezone';
import { toast } from '../../utils/toast';

const groupDescriptions = {
  'Change Team': 'Change Management Team',
  'Database Team': 'Database Management Team',
  'First Responders Group': 'Initial requests responders.',
  'HR Team': 'HR Team',
  'Network Support Team': 'Network Support Team',
  'Salesforce Team': 'Salesforce Team',
  'Security Team': 'Security Team',
  'DevOps Team': 'DevOps Team',
};

function getInitials(name) {
  return (name || '').split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

const EMAIL_NOTIF_RECIPIENTS = ['Requester', 'Assigned Agent', 'Agent Group', 'Manager', 'Vendor'];
const EMAIL_NOTIF_SECTIONS = [
  {
    title: 'Ticket Events',
    events: ['Ticket Created', 'Ticket Assigned'],
  },
  {
    title: 'Ticket Activity',
    events: ['Comment Added', 'SLA Warning', 'SLA Breach', 'Escalation', 'Pending Customer', 'Resolved', 'Closed', 'Reopened'],
  },
];
const EMAIL_NOTIF_TEMPLATES_STORAGE = 'powerdesk_email_notification_templates';

/** One template per event + recipient, e.g. Ticket Created|Requester */
function emailNotifTemplateKey(event, recipient) {
  return `${event}|${recipient}`;
}

const EMAIL_NOTIF_DEFAULTS = {
  'Ticket Created':   { 'Requester': true,  'Assigned Agent': false, 'Agent Group': true,  'Manager': false, 'Vendor': false },
  'Ticket Assigned':  { 'Requester': true,  'Assigned Agent': true,  'Agent Group': false, 'Manager': false, 'Vendor': false },
  'Comment Added':    { 'Requester': true,  'Assigned Agent': true,  'Agent Group': false, 'Manager': false, 'Vendor': false },
  'SLA Warning':      { 'Requester': false, 'Assigned Agent': true,  'Agent Group': true,  'Manager': false, 'Vendor': false },
  'SLA Breach':       { 'Requester': false, 'Assigned Agent': true,  'Agent Group': true,  'Manager': true,  'Vendor': false },
  'Escalation':       { 'Requester': true,  'Assigned Agent': true,  'Agent Group': true,  'Manager': true,  'Vendor': false },
  'Pending Customer': { 'Requester': true,  'Assigned Agent': true,  'Agent Group': false, 'Manager': false, 'Vendor': false },
  'Resolved':         { 'Requester': true,  'Assigned Agent': true,  'Agent Group': false, 'Manager': false, 'Vendor': false },
  'Closed':           { 'Requester': true,  'Assigned Agent': false, 'Agent Group': false, 'Manager': false, 'Vendor': false },
  'Reopened':         { 'Requester': true,  'Assigned Agent': true,  'Agent Group': true,  'Manager': false, 'Vendor': false },
};

const notificationItems = [
  { label: 'New ticket created', group: 'Requester Notification' },
  { label: 'Agent adds comment to Ticket', group: 'Requester Notification' },
  { label: 'Agent solves the Ticket', group: 'Requester Notification' },
  { label: 'Agent closes the Ticket', group: 'Requester Notification' },
  { label: 'Requested Item Stage Change', group: 'Requester Notification' },
  { label: 'Ticket Approved or Rejected', group: 'Requester Notification' },
  { label: 'User Invitation Email', group: 'Requester Notification', badge: 'SYSTEM' },
  { label: 'New ticket created', group: 'Agent Notification' },
  { label: 'Ticket assigned to Group', group: 'Agent Notification' },
  { label: 'Ticket assigned to Agent', group: 'Agent Notification' },
];

const workspaceTabs = ['Workspace managers', 'Workspace agents', 'Account-wide admins'];
const emailSections = ['Tickets', 'Problems', 'Changes', 'Releases', 'Assets', 'Contracts', 'Tasks', 'Purchase Orders', 'On-Call Schedules', 'Status Page'];

export default function AdminPage({ activeTab = 'Groups', setActiveTab = () => {} }) {
  const [activeWorkspaceTab, setActiveWorkspaceTab] = React.useState(workspaceTabs[0]);
  const [activeEmailSection, setActiveEmailSection] = React.useState(emailSections[0]);
  const [groups, setGroups] = React.useState([]);
  const [showCreateGroupModal, setShowCreateGroupModal] = React.useState(false);
  const [newGroupName, setNewGroupName] = React.useState('');
  const [newGroupDesc, setNewGroupDesc] = React.useState('');
  const [selectedAgents, setSelectedAgents] = React.useState([]);
  const [agentSearch, setAgentSearch] = React.useState('');
  const [agentDropdownOpen, setAgentDropdownOpen] = React.useState(false);
  const [editingGroup, setEditingGroup] = React.useState(null);
  const [editGroupName, setEditGroupName] = React.useState('');
  const [editGroupDesc, setEditGroupDesc] = React.useState('');
  const [editGroupAgents, setEditGroupAgents] = React.useState([]);
  const [editAgentSearch, setEditAgentSearch] = React.useState('');
  const [editAgentDropdownOpen, setEditAgentDropdownOpen] = React.useState(false);
  const [users, setUsers] = React.useState([]);
  const [allAgents, setAllAgents] = React.useState([]);
  const [openActionMenu, setOpenActionMenu] = React.useState(null);
  const [showAddUserModal, setShowAddUserModal] = React.useState(false);
  const [newUserName, setNewUserName] = React.useState('');
  const [newUserEmail, setNewUserEmail] = React.useState('');
  const [newUserRole, setNewUserRole] = React.useState('');
  const [newUserPassword, setNewUserPassword] = React.useState('');
  const [usersSubTab, setUsersSubTab] = React.useState('agents');
  const [showAddRequesterModal, setShowAddRequesterModal] = React.useState(false);
  const [newRequesterName, setNewRequesterName] = React.useState('');
  const [newRequesterEmail, setNewRequesterEmail] = React.useState('');
  const [newRequesterPhone, setNewRequesterPhone] = React.useState('');
  const [newRequesterCompany, setNewRequesterCompany] = React.useState('');
  const [newRequesterDept, setNewRequesterDept] = React.useState('');
  const [newRequesterPassword, setNewRequesterPassword] = React.useState('');
  const [newRequesterGroupId, setNewRequesterGroupId] = React.useState('');
  const [openRequesterMenu, setOpenRequesterMenu] = React.useState(null);

  // ── Edit Requester state ──────────────────────────────────────────────────
  const [editingRequester, setEditingRequester] = React.useState(null);
  const [editReqName, setEditReqName] = React.useState('');
  const [editReqEmail, setEditReqEmail] = React.useState('');
  const [editReqDept, setEditReqDept] = React.useState('');
  const [editReqPhone, setEditReqPhone] = React.useState('');
  const [editReqCompany, setEditReqCompany] = React.useState('');
  const [editReqGroupId, setEditReqGroupId] = React.useState('');
  const [editReqSaving, setEditReqSaving] = React.useState(false);

  // ── Requester Groups state ────────────────────────────────────────────────
  const [requesterGroups, setRequesterGroups] = React.useState([]);
  const [showCreateReqGroupModal, setShowCreateReqGroupModal] = React.useState(false);
  const [editingReqGroup, setEditingReqGroup] = React.useState(null);
  const [reqGroupName, setReqGroupName] = React.useState('');
  const [reqGroupDesc, setReqGroupDesc] = React.useState('');
  const [reqGroupMemberIds, setReqGroupMemberIds] = React.useState([]);
  const [notificationStates, setNotificationStates] = React.useState(
    Object.fromEntries(notificationItems.map(item => [item.label + '|' + item.group, true]))
  );
  const [emailNotifMatrix, setEmailNotifMatrix] = React.useState(() => {
    try {
      const raw = localStorage.getItem('powerdesk_email_notification_matrix');
      if (raw) return JSON.parse(raw);
    } catch (_) {}
    return JSON.parse(JSON.stringify(EMAIL_NOTIF_DEFAULTS));
  });
  const [notifSaved, setNotifSaved] = React.useState(false);
  const [editingTemplate, setEditingTemplate] = React.useState(null); // { item, key }
  const [savedTemplates, setSavedTemplates] = React.useState(() => {
    try {
      const raw = localStorage.getItem(EMAIL_NOTIF_TEMPLATES_STORAGE);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const persistEmailTemplate = React.useCallback((key, tpl) => {
    setSavedTemplates(prev => {
      const next = { ...prev, [key]: tpl };
      try {
        localStorage.setItem(EMAIL_NOTIF_TEMPLATES_STORAGE, JSON.stringify(next));
      } catch (_) {}
      return next;
    });
  }, []);
  const [viewingUser, setViewingUser] = React.useState(null);
  const [editingUser, setEditingUser] = React.useState(null);
  const [editUserName, setEditUserName] = React.useState('');
  const [editUserEmail, setEditUserEmail] = React.useState('');
  const [editUserRole, setEditUserRole] = React.useState('');

  // ── Audit Logs state ──────────────────────────────────────────────────────
  const [auditLogs, setAuditLogs] = React.useState([]);
  const [auditTotal, setAuditTotal] = React.useState(0);
  const [auditPage, setAuditPage] = React.useState(1);
  const [auditLoading, setAuditLoading] = React.useState(false);
  const [auditSearch, setAuditSearch] = React.useState('');
  const [auditAction, setAuditAction] = React.useState('all');
  const [auditEntityType, setAuditEntityType] = React.useState('all');
  const [auditDateFrom, setAuditDateFrom] = React.useState('');
  const [auditDateTo, setAuditDateTo] = React.useState('');
  const AUDIT_LIMIT = 50;

  // Load users and groups from API
  useEffect(() => {
    usersAPI.list()
      .then(data => {
        const mapped = (data || []).map(u => ({
          id: u.id,
          name: u.name || '',
          email: u.email || '',
          rawRole: u.role || 'agent',
          role: u.role === 'admin' ? 'IT Admin' : u.role === 'ceo' ? 'CEO / Admin' : u.role === 'manager' ? 'Manager' : u.role === 'lead' ? 'Team Lead' : u.role === 'supervisor' ? 'Supervisor' : u.role === 'agent' ? 'Agent' : u.role === 'requester' ? 'Requester' : u.role || 'Agent',
          dept: u.department || u.dept || '',
          phone: u.phone || '',
          company: u.company || '',
          lastActive: u.last_active ? formatInTz(u.last_active, { day: '2-digit', month: 'short', year: 'numeric' }) : 'Never',
          createdAt: u.created_at ? formatInTz(u.created_at, { day: '2-digit', month: 'short', year: 'numeric' }) : '—',
        }));
        setUsers(mapped);
        setAllAgents(mapped.filter(u => u.rawRole !== 'requester').map(u => u.name));
      })
      .catch(err => console.error('Failed to load users:', err));

    groupsAPI.list()
      .then(data => {
        setGroups((data || []).map(g => ({
          id: g.id,
          name: g.name || '',
          description: g.description || '',
          members: g.members || [],
        })));
      })
      .catch(err => console.error('Failed to load groups:', err));

    requesterGroupsAPI.list()
      .then(data => setRequesterGroups(data || []))
      .catch(err => console.error('Failed to load requester groups:', err));
  }, []);

  // ── Load audit logs when tab is active ────────────────────────────────────
  const fetchAuditLogs = React.useCallback(async (page = 1) => {
    setAuditLoading(true);
    try {
      const data = await auditAPI.list({
        page,
        limit: AUDIT_LIMIT,
        ...(auditSearch     ? { search:      auditSearch }     : {}),
        ...(auditAction !== 'all' ? { action: auditAction }    : {}),
        ...(auditEntityType !== 'all' ? { entity_type: auditEntityType } : {}),
        ...(auditDateFrom   ? { date_from:   auditDateFrom }   : {}),
        ...(auditDateTo     ? { date_to:     auditDateTo }     : {}),
      });
      setAuditLogs(data.logs || []);
      setAuditTotal(data.total || 0);
      setAuditPage(data.page || 1);
    } catch (err) {
      console.error('Failed to load audit logs:', err);
    } finally {
      setAuditLoading(false);
    }
  }, [auditSearch, auditAction, auditEntityType, auditDateFrom, auditDateTo]);

  useEffect(() => {
    if (activeTab === 'Audit Logs') {
      fetchAuditLogs(1);
    }
  }, [activeTab, fetchAuditLogs]);

  const handleAddUser = async () => {
    if (!newUserName.trim() || !newUserEmail.trim()) return;
    try {
      const created = await usersAPI.create({
        name: newUserName.trim(),
        email: newUserEmail.trim(),
        role: newUserRole.trim().toLowerCase() || 'agent',
        password: newUserPassword.trim() || undefined,
      });
      const rawRole = created.role || 'agent';
      setUsers(prev => [...prev, {
        id: created.id,
        name: created.name,
        email: created.email,
        rawRole,
        role: rawRole === 'admin' ? 'IT Admin' : rawRole === 'ceo' ? 'CEO / Admin' : rawRole === 'manager' ? 'Manager' : rawRole === 'lead' ? 'Team Lead' : rawRole === 'supervisor' ? 'Supervisor' : rawRole === 'agent' ? 'Agent' : rawRole === 'requester' ? 'Requester' : rawRole,
        lastActive: 'Just now',
      }]);
      if (rawRole !== 'requester') setAllAgents(prev => [...prev, created.name]);
      if (created.welcomeEmailSent) {
        toast(`User created. Welcome email sent to ${created.email}`, 'success');
      } else if (created.welcomeEmailWarning) {
        toast(`User created but email not sent: ${created.welcomeEmailWarning}`, 'warning');
      } else {
        toast('User created', 'success');
      }
    } catch (err) {
      toast('Failed to add user: ' + err.message, 'error');
    }
    setShowAddUserModal(false);
    setNewUserName(''); setNewUserEmail(''); setNewUserRole(''); setNewUserPassword('');
  };

  const handleSaveEditUser = async () => {
    if (!editUserName.trim() || !editUserEmail.trim()) return;
    try {
      await usersAPI.update(editingUser.id, {
        name: editUserName.trim(),
        email: editUserEmail.trim(),
        role: editUserRole.trim().toLowerCase() || editingUser.role.toLowerCase(),
      });
      setUsers(prev => prev.map(u => u.id === editingUser.id
        ? { ...u, name: editUserName.trim(), email: editUserEmail.trim(), role: editUserRole.trim() || u.role }
        : u
      ));
    } catch (err) {
      toast('Failed to update user: ' + err.message);
    }
    setEditingUser(null);
  };

  const handleDeactivateUser = async (userId) => {
    try {
      await usersAPI.delete(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err) {
      toast('Failed to remove user: ' + err.message);
    }
    setOpenActionMenu(null);
  };

  const handleAddRequester = async () => {
    if (!newRequesterName.trim() || !newRequesterEmail.trim()) return;
    try {
      const created = await usersAPI.create({
        name: newRequesterName.trim(),
        email: newRequesterEmail.trim(),
        role: 'requester',
        password: newRequesterPassword.trim() || undefined,
        phone: newRequesterPhone.trim() || undefined,
        company: newRequesterCompany.trim() || undefined,
        department: newRequesterDept.trim() || undefined,
      });
      const newUser = {
        id: created.id,
        name: created.name,
        email: created.email,
        rawRole: 'requester',
        role: 'Requester',
        dept: newRequesterDept.trim(),
        phone: newRequesterPhone.trim(),
        company: newRequesterCompany.trim(),
        lastActive: 'Just now',
        createdAt: 'Just now',
      };
      setUsers(prev => [...prev, newUser]);
      if (newRequesterGroupId) {
        const grp = requesterGroups.find(g => String(g.id) === newRequesterGroupId);
        if (grp) {
          const existingIds = (grp.members || []).map(m => m.id);
          await requesterGroupsAPI.update(grp.id, { memberIds: [...existingIds, created.id] });
          setRequesterGroups(prev => prev.map(g => g.id === grp.id
            ? { ...g, members: [...(g.members || []), { id: created.id, name: created.name, email: created.email }] }
            : g
          ));
        }
      }
      if (created.welcomeEmailSent) {
        toast(`Requester created. Welcome email sent to ${created.email}`, 'success');
      } else if (created.welcomeEmailWarning) {
        toast(`Requester created but email not sent: ${created.welcomeEmailWarning}`, 'warning');
      } else {
        toast('Requester created', 'success');
      }
    } catch (err) {
      toast('Failed to add requester: ' + err.message, 'error');
    }
    setShowAddRequesterModal(false);
    setNewRequesterName(''); setNewRequesterEmail('');
    setNewRequesterPhone(''); setNewRequesterCompany(''); setNewRequesterDept(''); setNewRequesterPassword(''); setNewRequesterGroupId('');
  };

  const handleDeactivateRequester = async (userId) => {
    try {
      await usersAPI.delete(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err) {
      toast('Failed to remove requester: ' + err.message);
    }
    setOpenRequesterMenu(null);
  };

  const openEditRequester = (user) => {
    const currentGroup = requesterGroups.find(g => (g.members || []).some(m => m.id === user.id));
    setEditingRequester(user);
    setEditReqName(user.name || '');
    setEditReqEmail(user.email || '');
    setEditReqDept(user.dept || '');
    setEditReqPhone(user.phone || '');
    setEditReqCompany(user.company || '');
    setEditReqGroupId(currentGroup ? String(currentGroup.id) : '');
    setOpenRequesterMenu(null);
  };

  const handleSaveRequester = async () => {
    if (!editReqName.trim() || !editReqEmail.trim()) { toast('Name and email are required.', 'warning'); return; }
    setEditReqSaving(true);
    try {
      await usersAPI.update(editingRequester.id, {
        name: editReqName.trim(),
        email: editReqEmail.trim(),
        department: editReqDept.trim() || undefined,
        phone: editReqPhone.trim() || undefined,
        company: editReqCompany.trim() || undefined,
      });

      // Handle group assignment
      const currentGroup = requesterGroups.find(g => (g.members || []).some(m => m.id === editingRequester.id));
      const newGroupId = editReqGroupId ? Number(editReqGroupId) : null;
      if (currentGroup && currentGroup.id !== newGroupId) {
        // Remove from old group
        const remaining = (currentGroup.members || []).filter(m => m.id !== editingRequester.id).map(m => m.id);
        await requesterGroupsAPI.update(currentGroup.id, { memberIds: remaining });
      }
      if (newGroupId && (!currentGroup || currentGroup.id !== newGroupId)) {
        // Add to new group
        const targetGroup = requesterGroups.find(g => g.id === newGroupId);
        const existing = (targetGroup?.members || []).map(m => m.id);
        if (!existing.includes(editingRequester.id)) {
          await requesterGroupsAPI.update(newGroupId, { memberIds: [...existing, editingRequester.id] });
        }
      }

      // Refresh data
      const freshGroups = await requesterGroupsAPI.list();
      setRequesterGroups(freshGroups || []);
      setUsers(prev => prev.map(u => u.id === editingRequester.id
        ? { ...u, name: editReqName.trim(), email: editReqEmail.trim(), dept: editReqDept.trim(), phone: editReqPhone.trim(), company: editReqCompany.trim() }
        : u
      ));
      setEditingRequester(null);
    } catch (err) {
      toast('Failed to save: ' + err.message);
    } finally {
      setEditReqSaving(false);
    }
  };

  const renderBreadcrumbs = (items) => {
    if (!items || items.length < 3) return null;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12, fontSize: 12, color: '#2563eb' }}>
        {items.map((item, index) => (
          <React.Fragment key={`${item}-${index}`}>
            <span style={{ color: index === items.length - 1 ? '#1A202C' : '#2563eb', fontWeight: index === items.length - 1 ? 700 : 600 }}>{item}</span>
            {index < items.length - 1 && <span style={{ color: '#94a3b8' }}>�</span>}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderGroups = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 18, flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#1A202C' }}>Agent Groups</div>
          <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>Organize groups, assign teams, and define how requests flow through your workspace.</div>
        </div>
        <button
          onClick={() => setShowCreateGroupModal(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%)', border: 'none', borderRadius: 8, color: '#ffffff', padding: '10px 16px', fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
        >
          <Icon name="plus" size={16} color="#fff" /> Create agent group
        </button>
      </div>

      <div style={{ display: 'grid', gap: 12, overflowY: 'auto', flex: 1, minHeight: 0, paddingRight: 2 }}>
        {groups.map(group => (
          <div key={group.name} style={{ background: '#FFFFFF', border: `1px solid ${T.border}`, borderRadius: 14, padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1A202C' }}>{group.name}</div>
              <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>{group.description}</div>
              {group.members.length > 0 && (
                <div style={{ fontSize: 12, color: T.active, marginTop: 4 }}>{group.members.length} agent{group.members.length !== 1 ? 's' : ''}</div>
              )}
            </div>
            <button
              onClick={() => {
                setEditingGroup(group);
                setEditGroupName(group.name);
                setEditGroupDesc(group.description);
                setEditGroupAgents((group.members || []).map(m => (typeof m === 'string' ? m : m.name)).filter(Boolean));
              }}
              style={{ border: 'none', background: 'transparent', color: T.active, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Icon name="edit" size={16} color={T.active} /> Edit
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  const openCreateReqGroup = () => {
    setEditingReqGroup(null);
    setReqGroupName('');
    setReqGroupDesc('');
    setReqGroupMemberIds([]);
    setShowCreateReqGroupModal(true);
  };

  const openEditReqGroup = (g) => {
    setEditingReqGroup(g);
    setReqGroupName(g.name || '');
    setReqGroupDesc(g.description || '');
    setReqGroupMemberIds((g.members || []).map(m => m.id));
    setShowCreateReqGroupModal(true);
  };

  const handleSaveReqGroup = async () => {
    if (!reqGroupName.trim()) { toast('Group name is required', 'warning'); return; }
    try {
      if (editingReqGroup) {
        await requesterGroupsAPI.update(editingReqGroup.id, {
          name: reqGroupName.trim(),
          description: reqGroupDesc,
          memberIds: reqGroupMemberIds,
        });
      } else {
        await requesterGroupsAPI.create({
          name: reqGroupName.trim(),
          description: reqGroupDesc,
          memberIds: reqGroupMemberIds,
        });
      }
      const fresh = await requesterGroupsAPI.list();
      setRequesterGroups(fresh || []);
      setShowCreateReqGroupModal(false);
    } catch (err) {
      toast(err.message || 'Failed to save group');
    }
  };

  const handleDeleteReqGroup = async (id) => {
    if (!window.confirm('Delete this requester group?')) return;
    try {
      await requesterGroupsAPI.delete(id);
      setRequesterGroups(prev => prev.filter(g => g.id !== id));
    } catch (err) {
      toast(err.message || 'Failed to delete');
    }
  };

  const renderRequesterGroups = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 18, flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#1A202C' }}>Requester Groups</div>
          <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>Organisational grouping for requesters (e.g. by team, department, or company).</div>
        </div>
        <button
          onClick={openCreateReqGroup}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%)', border: 'none', borderRadius: 8, color: '#ffffff', padding: '10px 16px', fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
        >
          <Icon name="plus" size={16} color="#fff" /> Create requester group
        </button>
      </div>

      <div style={{ display: 'grid', gap: 12, overflowY: 'auto', flex: 1, minHeight: 0, paddingRight: 2, alignContent: 'start' }}>
        {requesterGroups.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 16px', color: T.muted, background: '#FFFFFF', border: `1px dashed ${T.border}`, borderRadius: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#2D3748', marginBottom: 6 }}>No requester groups yet</div>
            <div style={{ fontSize: 13 }}>Create your first group to organise requesters.</div>
          </div>
        )}
        {requesterGroups.map(group => (
          <div key={group.id} style={{ background: '#FFFFFF', border: `1px solid ${T.border}`, borderRadius: 14, padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1A202C' }}>{group.name}</div>
              {group.description && <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>{group.description}</div>}
              <div style={{ fontSize: 12, color: T.active, marginTop: 4 }}>
                {(group.members || []).length} requester{(group.members || []).length !== 1 ? 's' : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => openEditReqGroup(group)}
                style={{ border: 'none', background: 'transparent', color: T.active, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <Icon name="edit" size={16} color={T.active} /> Edit
              </button>
              <button
                onClick={() => handleDeleteReqGroup(group.id)}
                style={{ border: 'none', background: 'transparent', color: '#ef4444', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <Icon name="close" size={16} color="#ef4444" /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderUserTable = (userList, menuState, setMenuState, onDeactivate, onEdit, onView) => (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', border: `1px solid ${T.border}`, borderRadius: 12 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#FFFFFF', position: 'sticky', top: 0, zIndex: 2 }}>
            <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 700, color: '#2D3748', borderBottom: `1px solid ${T.border}`, width: '32%' }}>Name</th>
            <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 700, color: '#2D3748', borderBottom: `1px solid ${T.border}`, width: '36%' }}>Email</th>
            <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 700, color: '#2D3748', borderBottom: `1px solid ${T.border}`, width: '22%' }}>Last Active</th>
            <th style={{ padding: '11px 16px', textAlign: 'center', fontWeight: 700, color: '#2D3748', borderBottom: `1px solid ${T.border}`, width: '10%' }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {userList.length === 0 && (
            <tr><td colSpan={4} style={{ textAlign: 'center', padding: '40px 16px', color: T.muted, fontSize: 13 }}>No users found.</td></tr>
          )}
          {userList.map((user) => (
            <tr key={user.email} style={{ borderBottom: `1px solid ${T.border}` }}>
              <td style={{ padding: '11px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 9, background: T.g50, display: 'grid', placeItems: 'center', color: T.active, fontWeight: 800, fontSize: 12, flexShrink: 0 }}>
                    {user.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: '#1A202C', fontSize: 13 }}>{user.name}</div>
                    {user.role && <div style={{ fontSize: 11, color: T.muted }}>{user.role}</div>}
                  </div>
                </div>
              </td>
              <td style={{ padding: '11px 16px', color: '#2D3748' }}>{user.email}</td>
              <td style={{ padding: '11px 16px', color: T.muted }}>{user.lastActive}</td>
              <td style={{ padding: '11px 16px', textAlign: 'center', position: 'relative' }}>
                <button
                  onClick={() => setMenuState(menuState === user.email ? null : user.email)}
                  title="Actions"
                  style={{ border: `1px solid ${T.border}`, background: '#FFFFFF', cursor: 'pointer', borderRadius: 6, padding: '6px 8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <Icon name="moreH" size={16} color="#2D3748" />
                </button>
                {menuState === user.email && (
                  <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setMenuState(null)} />
                    <div style={{ position: 'absolute', right: 8, top: 'calc(100% - 4px)', zIndex: 100, background: '#FFFFFF', border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: '0 6px 20px rgba(0,0,0,0.12)', minWidth: 150, overflow: 'hidden' }}>
                      <button onClick={() => { onView(user); setMenuState(null); }} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#1A202C' }}>
                        <Icon name="eye" size={14} color="#2D3748" /> View
                      </button>
                      <button onClick={() => { onEdit(user); setMenuState(null); }} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#1A202C', borderTop: `1px solid ${T.border}` }}>
                        <Icon name="edit" size={14} color="#2D3748" /> Edit
                      </button>
                      <button onClick={() => onDeactivate(user.id)} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#ef4444', borderTop: `1px solid ${T.border}` }}>
                        <Icon name="close" size={14} color="#ef4444" /> Deactivate
                      </button>
                    </div>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderUsers = (forcedSub = null) => {
    const agentUsers = users.filter(u => ['admin','ceo','manager','lead','supervisor','agent'].includes(u.rawRole));
    const requesterUsers = users.filter(u => u.rawRole === 'requester');
    const activeSub = forcedSub || usersSubTab;

    return (
      <>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 14, flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#1A202C' }}>{forcedSub === 'agents' ? 'Agents' : forcedSub === 'requesters' ? 'Requesters' : 'Workspace Users'}</div>
            <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>{forcedSub === 'agents' ? 'Manage agents, admins, and supervisors.' : forcedSub === 'requesters' ? 'Manage requesters who submit tickets.' : 'Manage agents, admins, and requesters.'}</div>
          </div>
          {activeSub === 'agents' ? (
            <button
              onClick={() => setShowAddUserModal(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%)', border: 'none', borderRadius: 8, color: '#ffffff', padding: '10px 16px', fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
            >
              <Icon name="plus" size={16} color="#fff" /> Add Agent
            </button>
          ) : (
            <button
              onClick={() => setShowAddRequesterModal(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%)', border: 'none', borderRadius: 8, color: '#ffffff', padding: '10px 16px', fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
            >
              <Icon name="plus" size={16} color="#fff" /> Add Requester
            </button>
          )}
        </div>

        {/* Sub-tabs (hidden when forced) */}
        {!forcedSub && (
        <div style={{ display: 'flex', gap: 0, borderBottom: `2px solid ${T.border}`, marginBottom: 14, flexShrink: 0 }}>
          {[
            { key: 'agents', label: 'Agents & Admins', count: agentUsers.length },
            { key: 'requesters', label: 'Requesters', count: requesterUsers.length },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setUsersSubTab(tab.key)}
              style={{
                padding: '9px 18px',
                border: 'none',
                background: 'transparent',
                fontSize: 13,
                fontWeight: usersSubTab === tab.key ? 700 : 500,
                color: usersSubTab === tab.key ? T.active : T.muted,
                cursor: 'pointer',
                borderBottom: usersSubTab === tab.key ? `2px solid ${T.active}` : '2px solid transparent',
                marginBottom: -2,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {tab.label}
              <span style={{
                fontSize: 11,
                fontWeight: 700,
                background: usersSubTab === tab.key ? T.g100 : '#F0F2F5',
                color: usersSubTab === tab.key ? T.active : T.muted,
                padding: '1px 7px',
                borderRadius: 99,
              }}>{tab.count}</span>
            </button>
          ))}
        </div>
        )}

        {/* Agents & Admins Table */}
        {activeSub === 'agents' && renderUserTable(
          agentUsers,
          openActionMenu,
          setOpenActionMenu,
          handleDeactivateUser,
          (user) => { setEditingUser(user); setEditUserName(user.name); setEditUserEmail(user.email); setEditUserRole(user.role || ''); },
          (user) => setViewingUser(user),
        )}

        {/* Requesters Table */}
        {activeSub === 'requesters' && (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', border: `1px solid ${T.border}`, borderRadius: 12 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#FFFFFF', position: 'sticky', top: 0, zIndex: 2 }}>
                    <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 700, color: '#2D3748', borderBottom: `1px solid ${T.border}`, width: '22%' }}>Name</th>
                    <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 700, color: '#2D3748', borderBottom: `1px solid ${T.border}`, width: '26%' }}>Email</th>
                    <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 700, color: '#2D3748', borderBottom: `1px solid ${T.border}`, width: '16%' }}>Department</th>
                    <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 700, color: '#2D3748', borderBottom: `1px solid ${T.border}`, width: '18%' }}>Requester Group</th>
                    <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 700, color: '#2D3748', borderBottom: `1px solid ${T.border}`, width: '10%' }}>Last Active</th>
                    <th style={{ padding: '11px 16px', textAlign: 'center', fontWeight: 700, color: '#2D3748', borderBottom: `1px solid ${T.border}`, width: '8%' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {requesterUsers.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', padding: '48px 16px', color: T.muted }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#2D3748', marginBottom: 6 }}>No requesters yet</div>
                        <div style={{ fontSize: 13 }}>Add requesters who submit tickets through your service desk.</div>
                      </td>
                    </tr>
                  )}
                  {requesterUsers.map((user) => {
                    const userGroup = requesterGroups.find(g => (g.members || []).some(m => m.id === user.id));
                    return (
                    <tr key={user.email} style={{ borderBottom: `1px solid ${T.border}` }}>
                      <td style={{ padding: '11px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 34, height: 34, borderRadius: 9, background: '#eff6ff', display: 'grid', placeItems: 'center', color: '#2563eb', fontWeight: 800, fontSize: 12, flexShrink: 0 }}>
                            {user.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, color: '#1A202C', fontSize: 13 }}>{user.name}</div>
                            <div style={{ fontSize: 11, color: '#2563eb' }}>Requester</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '11px 16px', color: '#2D3748' }}>{user.email}</td>
                      <td style={{ padding: '11px 16px', color: T.muted }}>{user.dept || '--'}</td>
                      <td style={{ padding: '11px 16px' }}>
                        {userGroup
                          ? <span style={{ background: '#EFF6FF', color: '#2563eb', fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 99, whiteSpace: 'nowrap' }}>{userGroup.name}</span>
                          : <span style={{ color: T.muted, fontSize: 12 }}>--</span>
                        }
                      </td>
                      <td style={{ padding: '11px 16px', color: T.muted }}>{user.lastActive}</td>
                      <td style={{ padding: '11px 16px', textAlign: 'center', position: 'relative' }}>
                        <button
                          onClick={() => setOpenRequesterMenu(openRequesterMenu === user.email ? null : user.email)}
                          title="Actions"
                          style={{ border: `1px solid ${T.border}`, background: '#FFFFFF', cursor: 'pointer', borderRadius: 6, padding: '6px 8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <Icon name="moreV" size={16} color="#2D3748" />
                        </button>
                        {openRequesterMenu === user.email && (
                          <>
                            <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setOpenRequesterMenu(null)} />
                            <div style={{ position: 'absolute', right: 8, top: 'calc(100% - 4px)', zIndex: 100, background: '#FFFFFF', border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: '0 6px 20px rgba(0,0,0,0.12)', minWidth: 150, overflow: 'hidden' }}>
                              <button onClick={() => { setViewingUser(user); setOpenRequesterMenu(null); }} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#1A202C' }}>
                                <Icon name="eye" size={14} color="#2D3748" /> View
                              </button>
                              <button onClick={() => openEditRequester(user)} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#1A202C', borderTop: `1px solid ${T.border}` }}>
                                <Icon name="edit" size={14} color="#2D3748" /> Edit
                              </button>
                              <button onClick={() => handleDeactivateRequester(user.id)} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#ef4444', borderTop: `1px solid ${T.border}` }}>
                                <Icon name="close" size={14} color="#ef4444" /> Deactivate
                              </button>
                            </div>
                          </>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ── Edit Requester Modal ─────────────────────────────────────────── */}
      {editingRequester && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 480, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: '#1A202C' }}>Edit Requester</div>
              {editingRequester.createdAt && editingRequester.createdAt !== '—' && (
                <div style={{ fontSize: 11, color: T.muted, textAlign: 'right' }}>
                  <span style={{ fontWeight: 600 }}>Created</span><br />{editingRequester.createdAt}
                </div>
              )}
            </div>
            {[
              { label: 'Name *', value: editReqName, set: setEditReqName, placeholder: 'Full name' },
              { label: 'Email *', value: editReqEmail, set: setEditReqEmail, placeholder: 'Email address' },
            ].map(({ label, value, set, placeholder }) => (
              <div key={label} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#4A5568', marginBottom: 5 }}>{label}</div>
                <input
                  value={value}
                  onChange={e => set(e.target.value)}
                  placeholder={placeholder}
                  style={{ width: '100%', padding: '9px 12px', border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
            ))}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#4A5568', marginBottom: 5 }}>Department</div>
              <select
                value={editReqDept}
                onChange={e => setEditReqDept(e.target.value)}
                style={{ width: '100%', padding: '9px 12px', border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, outline: 'none', background: '#fff' }}
              >
                <option value="">-- Select Department --</option>
                {(masterData.department || []).map(d => (
                  <option key={d.id} value={d.value}>{d.value}</option>
                ))}
              </select>
            </div>
            {[
              { label: 'Phone', value: editReqPhone, set: setEditReqPhone, placeholder: 'Phone number' },
              { label: 'Company', value: editReqCompany, set: setEditReqCompany, placeholder: 'Company name' },
            ].map(({ label, value, set, placeholder }) => (
              <div key={label} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#4A5568', marginBottom: 5 }}>{label}</div>
                <input
                  value={value}
                  onChange={e => set(e.target.value)}
                  placeholder={placeholder}
                  style={{ width: '100%', padding: '9px 12px', border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
            ))}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#4A5568', marginBottom: 5 }}>Requester Group</div>
              <select
                value={editReqGroupId}
                onChange={e => setEditReqGroupId(e.target.value)}
                style={{ width: '100%', padding: '9px 12px', border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, outline: 'none', background: '#fff' }}
              >
                <option value="">-- No Group --</option>
                {requesterGroups.map(g => (
                  <option key={g.id} value={String(g.id)}>{g.name}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditingRequester(null)} style={{ padding: '9px 20px', borderRadius: 8, border: `1px solid ${T.border}`, background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#4A5568' }}>Cancel</button>
              <button onClick={handleSaveRequester} disabled={editReqSaving} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: T.active, color: '#fff', fontSize: 13, fontWeight: 700, cursor: editReqSaving ? 'wait' : 'pointer' }}>
                {editReqSaving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
      </>
    );
  };

  const renderEmailNotifications = () => {
    if (editingTemplate) {
      return (
        <EmailTemplateEditor
          item={editingTemplate.item}
          templateKey={editingTemplate.key}
          recipient={editingTemplate.recipient}
          templates={savedTemplates}
          onSave={persistEmailTemplate}
          onBack={() => setEditingTemplate(null)}
        />
      );
    }

    const toggleCell = (event, recipient) => {
      setEmailNotifMatrix(prev => ({
        ...prev,
        [event]: { ...prev[event], [recipient]: !prev[event][recipient] },
      }));
      setNotifSaved(false);
    };

    const RecipientCell = ({ event, recipient }) => {
      const on = emailNotifMatrix[event]?.[recipient];
      const tKey = emailNotifTemplateKey(event, recipient);
      const customized = !!savedTemplates[tKey];
      return (
        <td style={{ textAlign: 'center', padding: '8px 4px', verticalAlign: 'middle' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <button
              type="button"
              onClick={() => toggleCell(event, recipient)}
              title={on ? 'Notification on — click to turn off' : 'Notification off — click to turn on'}
              style={{
                border: 'none', background: 'transparent', padding: 0, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              {on
                ? <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: 6, background: '#dcfce7', color: '#16a34a', fontSize: 15, fontWeight: 900 }}>✓</span>
                : <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: 6, background: '#fee2e2', color: '#ef4444', fontSize: 15, fontWeight: 900 }}>✕</span>
              }
            </button>
            <button
              type="button"
              onClick={() => setEditingTemplate({
                item: { label: event, group: `${recipient} email` },
                key: tKey,
                recipient,
                event,
              })}
              title={`Customize ${recipient} email for "${event}" — use dynamic fields like {{requester_name}}`}
              aria-label={`Edit ${recipient} template for ${event}`}
              style={{
                border: `1px solid ${customized ? '#86efac' : '#bfdbfe'}`,
                background: customized ? '#dcfce7' : '#eff6ff',
                borderRadius: 6,
                width: 28,
                height: 24,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <Icon name="edit" size={12} color={customized ? '#16a34a' : T.active} />
            </button>
          </div>
        </td>
      );
    };

    const MatrixTable = ({ events }) => (
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <thead>
          <tr style={{ background: T.g50 }}>
            <th style={{ textAlign: 'left', padding: '10px 14px', fontSize: 12, fontWeight: 700, color: '#2D3748', width: '32%' }}>Event</th>
            {EMAIL_NOTIF_RECIPIENTS.map(r => (
              <th key={r} style={{ textAlign: 'center', padding: '10px 6px', fontSize: 11, fontWeight: 700, color: '#2D3748' }}>
                <div>{r}</div>
                <div style={{ fontSize: 10, fontWeight: 500, color: T.muted, marginTop: 4 }}>✓/✕ then ✎ template</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {events.map((event, idx) => (
            <tr key={event} style={{ borderTop: `1px solid ${T.border}`, background: idx % 2 === 1 ? '#FAFBFC' : '#FFFFFF' }}>
              <td style={{ padding: '10px 14px', fontSize: 13, color: '#1A202C', fontWeight: 500 }}>{event}</td>
              {EMAIL_NOTIF_RECIPIENTS.map(r => <RecipientCell key={r} event={event} recipient={r} />)}
            </tr>
          ))}
        </tbody>
      </table>
    );

    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#1A202C' }}>Email Notifications</div>
            <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
              <strong>✓ / ✕</strong> = send to that role. <strong>✎</strong> under each column = edit that role&apos;s email template with dynamic fields (e.g. <code style={{ fontSize: 12 }}>{'{{requester_name}}'}</code> for Requester).
            </div>
          </div>
          <button
            onClick={() => {
              try {
                localStorage.setItem('powerdesk_email_notification_matrix', JSON.stringify(emailNotifMatrix));
              } catch (_) {}
              setNotifSaved(true);
              setTimeout(() => setNotifSaved(false), 2500);
            }}
            style={{ border: 'none', borderRadius: 8, padding: '9px 22px', background: notifSaved ? '#16a34a' : T.active, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'background .2s', flexShrink: 0 }}
          >
            {notifSaved ? '✓ Saved' : 'Save Changes'}
          </button>
        </div>

        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16, padding: '12px 16px',
          background: '#F8FAFC', border: `1px solid ${T.border}`, borderRadius: 10, fontSize: 12, color: '#4A5568',
        }}>
          <span><strong style={{ color: '#16a34a' }}>✓</strong> Send to recipient</span>
          <span><strong style={{ color: '#ef4444' }}>✕</strong> Do not send</span>
          <span><strong>✎</strong> Edit <strong>Requester</strong> / <strong>Agent</strong> template (per column)</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 28, height: 22, borderRadius: 6, background: '#eff6ff', border: '1px solid #bfdbfe', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="edit" size={12} color={T.active} />
            </span>
            Default
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 28, height: 22, borderRadius: 6, background: '#dcfce7', border: '1px solid #86efac', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="edit" size={12} color="#16a34a" />
            </span>
            Saved custom
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {EMAIL_NOTIF_SECTIONS.map(section => (
            <div key={section.title} style={{ background: '#FFFFFF', border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', borderBottom: `1px solid ${T.border}`, fontSize: 13, fontWeight: 700, color: '#2D3748', background: '#F8FAFC' }}>
                {section.title}
              </div>
              <MatrixTable events={section.events} />
            </div>
          ))}
        </div>
      </>
    );
  };

  const renderWorkspaceSettings = () => {
    const workspaceManagersList = users.filter(u => ['ceo','manager','lead','supervisor','admin'].includes(u.rawRole));
    const workspaceAgentsList   = users.filter(u => u.rawRole === 'agent');
    const accountAdminsList     = users.filter(u => ['ceo','admin','manager'].includes(u.rawRole));
    return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#1A202C' }}>Workspace Settings</div>
          <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>Configure workspace roles and permissions.</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
        {workspaceTabs.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveWorkspaceTab(tab)}
            style={{
              borderRadius: 999,
              border: `1px solid ${activeWorkspaceTab === tab ? T.active : 'transparent'}`,
              background: activeWorkspaceTab === tab ? '#fff' : T.g50,
              color: activeWorkspaceTab === tab ? '#1A202C' : T.muted,
              padding: '8px 14px',
              cursor: 'pointer',
              fontWeight: activeWorkspaceTab === tab ? 700 : 600,
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      <div style={{ background: '#FFFFFF', border: `1px solid ${T.border}`, borderRadius: 14, padding: 18 }}>
        {activeWorkspaceTab === 'Workspace managers' && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: '#1A202C' }}>Workspace managers</div>
            {workspaceManagersList.length === 0 && <div style={{ color: T.muted, fontSize: 13, padding: '12px 0' }}>No workspace managers found.</div>}
            {workspaceManagersList.map(agent => (
              <div key={agent.email} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '14px 0', borderBottom: `1px solid ${T.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 42, height: 42, borderRadius: 12, background: T.g50, display: 'grid', placeItems: 'center', color: T.active, fontWeight: 800 }}>{getInitials(agent.name)}</div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#1A202C' }}>{agent.name}</div>
                    <div style={{ fontSize: 13, color: T.muted }}>{agent.email}</div>
                    <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{agent.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeWorkspaceTab === 'Workspace agents' && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: '#1A202C' }}>Workspace agents</div>
            {workspaceAgentsList.length === 0 && <div style={{ color: T.muted, fontSize: 13, padding: '12px 0' }}>No agents found.</div>}
            {workspaceAgentsList.map(agent => (
              <div key={agent.email} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '14px 0', borderBottom: `1px solid ${T.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 42, height: 42, borderRadius: 12, background: T.g50, display: 'grid', placeItems: 'center', color: T.active, fontWeight: 800 }}>{getInitials(agent.name)}</div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#1A202C' }}>{agent.name}</div>
                    <div style={{ fontSize: 13, color: T.muted }}>{agent.email}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeWorkspaceTab === 'Account-wide admins' && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: '#1A202C' }}>Account-wide admins</div>
            {accountAdminsList.length === 0 && <div style={{ color: T.muted, fontSize: 13, padding: '12px 0' }}>No admins found.</div>}
            {accountAdminsList.map(agent => (
              <div key={agent.email} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '14px 0', borderBottom: `1px solid ${T.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 42, height: 42, borderRadius: 12, background: T.g50, display: 'grid', placeItems: 'center', color: T.active, fontWeight: 800 }}>{getInitials(agent.name)}</div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#1A202C' }}>{agent.name}</div>
                    <div style={{ fontSize: 13, color: T.muted }}>{agent.email}</div>
                    <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{agent.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
    );
  };

  // --- Master Data state -------------------------------------------------------
  const [masterData, setMasterData] = React.useState({});
  const [masterTab, setMasterTab] = React.useState('status');
  const [showAddMasterModal, setShowAddMasterModal] = React.useState(false);
  const [newMasterValue, setNewMasterValue] = React.useState('');
  const [newMasterColor, setNewMasterColor] = React.useState('#718096');
  const [newMasterParent, setNewMasterParent] = React.useState('');
  const [masterLoading, setMasterLoading] = React.useState(false);
  const [editingMasterItem, setEditingMasterItem] = React.useState(null);
  const [editMasterValue, setEditMasterValue] = React.useState('');
  const [editMasterColor, setEditMasterColor] = React.useState('#718096');
  const [editMasterParent, setEditMasterParent] = React.useState('');
  const [editMasterLoading, setEditMasterLoading] = React.useState(false);

  const MASTER_TYPES = [
    { key: 'status',                   label: 'Status' },
    { key: 'priority',                 label: 'Priority' },
    { key: 'source',                   label: 'Source' },
    { key: 'type',                     label: 'Type' },
    { key: 'department',               label: 'Department' },
    { key: 'org_unit',                 label: 'Organization Unit' },
    { key: 'head_of_department',       label: 'Head of Department' },
    { key: 'category',                 label: 'Category' },
    { key: 'sub_category',             label: 'Sub-Category' },
    { key: 'additional_sub_category',  label: 'Additional Sub-Category' },
  ];

  React.useEffect(() => {
    if (activeTab === 'Master Data' || activeTab === 'Users' || activeTab === 'Agents' || activeTab === 'Requesters') {
      masterAPI.all().then(setMasterData).catch(() => {});
    }
  }, [activeTab]);

  const handleAddMasterItem = async () => {
    if (!newMasterValue.trim()) return;
    setMasterLoading(true);
    try {
      const payload = { field_type: masterTab, value: newMasterValue.trim(), color: newMasterColor };
      if ((masterTab === 'sub_category' || masterTab === 'additional_sub_category') && newMasterParent) payload.parent_value = newMasterParent;
      const created = await masterAPI.create(payload);
      setMasterData(prev => ({
        ...prev,
        [masterTab]: [...(prev[masterTab] || []), created],
      }));
      setNewMasterValue('');
      setNewMasterColor('#718096');
      setNewMasterParent('');
      setShowAddMasterModal(false);
    } catch (err) {
      toast(err.message);
    }
    setMasterLoading(false);
  };

  const handleToggleMasterItem = async (item) => {
    try {
      const updated = await masterAPI.update(item.id, { is_active: !item.is_active });
      setMasterData(prev => ({
        ...prev,
        [masterTab]: (prev[masterTab] || []).map(d => d.id === item.id ? updated : d),
      }));
    } catch (err) { toast(err.message); }
  };

  const handleDeleteMasterItem = async (item) => {
    if (!window.confirm(`Delete "${item.value}"?`)) return;
    try {
      await masterAPI.delete(item.id);
      setMasterData(prev => ({
        ...prev,
        [masterTab]: (prev[masterTab] || []).filter(d => d.id !== item.id),
      }));
    } catch (err) { toast(err.message); }
  };

  const openEditMasterModal = (item) => {
    setEditingMasterItem(item);
    setEditMasterValue(item.value);
    setEditMasterColor(item.color || '#718096');
    setEditMasterParent(item.parent_value || '');
  };

  const handleSaveMasterItem = async () => {
    if (!editMasterValue.trim()) return;
    setEditMasterLoading(true);
    try {
      const payload = { value: editMasterValue.trim(), color: editMasterColor };
      if (masterTab === 'sub_category' || masterTab === 'additional_sub_category') payload.parent_value = editMasterParent || null;
      const updated = await masterAPI.update(editingMasterItem.id, payload);
      setMasterData(prev => ({
        ...prev,
        [masterTab]: (prev[masterTab] || []).map(d => d.id === editingMasterItem.id ? updated : d),
      }));
      setEditingMasterItem(null);
      toast('Updated successfully', 'success');
    } catch (err) { toast(err.message || 'Failed to update'); }
    setEditMasterLoading(false);
  };

  const renderMasterData = () => {
    const items = masterData[masterTab] || [];
    const currentTypeLabel = MASTER_TYPES.find(t => t.key === masterTab)?.label || masterTab;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 14, flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#1A202C' }}>Master Data</div>
            <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>Manage dropdown values for ticket fields across your service desk.</div>
          </div>
          <button
            onClick={() => setShowAddMasterModal(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%)', border: 'none', borderRadius: 8, color: '#ffffff', padding: '10px 16px', fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
          >
            <Icon name="plus" size={16} color="#fff" /> Add {currentTypeLabel}
          </button>
        </div>

        {/* Type tabs */}
        <div style={{ display: 'flex', gap: 0, borderBottom: `2px solid ${T.border}`, marginBottom: 14, flexShrink: 0, overflowX: 'auto' }}>
          {MASTER_TYPES.map(t => (
            <button key={t.key} onClick={() => { setMasterTab(t.key); setNewMasterParent(''); }} style={{
              padding: '9px 16px', border: 'none', background: 'transparent', fontSize: 13,
              fontWeight: masterTab === t.key ? 700 : 500,
              color: masterTab === t.key ? T.active : T.muted,
              cursor: 'pointer', whiteSpace: 'nowrap',
              borderBottom: masterTab === t.key ? `2px solid ${T.active}` : '2px solid transparent', marginBottom: -2,
            }}>{t.label}</button>
          ))}
        </div>

        {/* Table */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', border: `1px solid ${T.border}`, borderRadius: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#FFFFFF', position: 'sticky', top: 0, zIndex: 2 }}>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 700, color: '#2D3748', borderBottom: `1px solid ${T.border}`, width: (masterTab === 'sub_category' || masterTab === 'additional_sub_category') ? '30%' : '40%' }}>Value</th>
                {(masterTab === 'sub_category' || masterTab === 'additional_sub_category') && <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 700, color: '#2D3748', borderBottom: `1px solid ${T.border}`, width: '20%' }}>{masterTab === 'sub_category' ? 'Category' : 'Sub-Category'}</th>}
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 700, color: '#2D3748', borderBottom: `1px solid ${T.border}`, width: '15%' }}>Color</th>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 700, color: '#2D3748', borderBottom: `1px solid ${T.border}`, width: '15%' }}>Status</th>
                <th style={{ padding: '11px 16px', textAlign: 'center', fontWeight: 700, color: '#2D3748', borderBottom: `1px solid ${T.border}`, width: '20%' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={(masterTab === 'sub_category' || masterTab === 'additional_sub_category') ? 5 : 4} style={{ textAlign: 'center', padding: '40px 16px', color: T.muted }}>No {currentTypeLabel.toLowerCase()} values yet. Add one above.</td></tr>
              )}
              {items.map(item => (
                <tr key={item.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                  <td style={{ padding: '11px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: item.color || '#718096', display: 'inline-block', flexShrink: 0 }} />
                      <span style={{ fontWeight: 600, color: '#1A202C' }}>{item.value}</span>
                    </div>
                  </td>
                  {(masterTab === 'sub_category' || masterTab === 'additional_sub_category') && (
                    <td style={{ padding: '11px 16px' }}>
                      {item.parent_value
                        ? <span style={{ fontSize: 12, background: '#EEF2FF', color: '#3B5BDB', padding: '2px 9px', borderRadius: 5, fontWeight: 600 }}>{item.parent_value}</span>
                        : <span style={{ color: T.muted, fontSize: 12 }}>—</span>}
                    </td>
                  )}
                  <td style={{ padding: '11px 16px' }}>
                    <span style={{ fontSize: 12, background: (item.color || '#718096') + '22', color: item.color || '#718096', padding: '2px 8px', borderRadius: 5, fontWeight: 600, border: `1px solid ${(item.color || '#718096')}44` }}>{item.color || '�'}</span>
                  </td>
                  <td style={{ padding: '11px 16px' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, background: item.is_active ? '#E0E7FF' : '#f1f5f9', color: item.is_active ? '#2C41A8' : '#64748b', padding: '3px 10px', borderRadius: 99 }}>
                      {item.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '11px 16px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                      <button onClick={() => openEditMasterModal(item)} style={{ padding: '4px 8px', border: '1px solid #bfdbfe', borderRadius: 6, background: '#eff6ff', fontSize: 12, cursor: 'pointer', color: '#2563eb', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Icon name="edit" size={13} color="#2563eb" />
                      </button>
                      <button onClick={() => handleToggleMasterItem(item)} style={{ padding: '4px 10px', border: `1px solid ${T.border}`, borderRadius: 6, background: '#FFFFFF', fontSize: 12, cursor: 'pointer', color: item.is_active ? '#b45309' : T.active, fontWeight: 600 }}>
                        {item.is_active ? 'Disable' : 'Enable'}
                      </button>
                      <button onClick={() => handleDeleteMasterItem(item)} style={{ padding: '4px 8px', border: '1px solid #fca5a5', borderRadius: 6, background: '#fff5f5', fontSize: 12, cursor: 'pointer', color: '#dc2626', fontWeight: 600 }}>
                        <Icon name="trash" size={13} color="#dc2626" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Add master item modal */}
        {showAddMasterModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#FFFFFF', borderRadius: 16, padding: 28, width: 420, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#1A202C' }}>Add {currentTypeLabel}</div>
                <button onClick={() => setShowAddMasterModal(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}>
                  <Icon name="close" size={18} color={T.muted} />
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {masterTab === 'sub_category' && (
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Category</label>
                    <select
                      value={newMasterParent}
                      onChange={e => setNewMasterParent(e.target.value)}
                      style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit', background: '#fff' }}
                    >
                      <option value=''>— Select category (optional) —</option>
                      {(masterData['category'] || []).filter(c => c.is_active).map(c => (
                        <option key={c.id} value={c.value}>{c.value}</option>
                      ))}
                    </select>
                  </div>
                )}
                {masterTab === 'additional_sub_category' && (
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Sub-Category <span style={{ color: '#ef4444' }}>*</span></label>
                    <select
                      value={newMasterParent}
                      onChange={e => setNewMasterParent(e.target.value)}
                      style={{ width: '100%', boxSizing: 'border-box', border: `1.5px solid ${newMasterParent ? T.border : '#ef4444'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit', background: '#fff' }}
                    >
                      <option value=''>— Select sub-category —</option>
                      {(masterData['sub_category'] || []).filter(c => c.is_active).map(c => (
                        <option key={c.id} value={c.value}>{c.value}{c.parent_value ? ` (${c.parent_value})` : ''}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Value <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    autoFocus
                    value={newMasterValue}
                    onChange={e => setNewMasterValue(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddMasterItem()}
                    placeholder={`e.g. ${masterTab === 'status' ? 'On Hold' : masterTab === 'priority' ? 'Critical' : masterTab === 'department' ? 'Design' : masterTab === 'sub_category' ? 'Driver Issues' : 'New value'}`}
                    style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Color</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input type="color" value={newMasterColor} onChange={e => setNewMasterColor(e.target.value)}
                      style={{ width: 48, height: 36, border: `1px solid ${T.border}`, borderRadius: 6, cursor: 'pointer', padding: 2 }} />
                    <span style={{ fontSize: 13, color: T.muted }}>{newMasterColor}</span>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {['#3B5BDB', '#2563eb', '#f59e0b', '#ef4444', '#8b5cf6', '#0891b2', '#dc2626', '#718096'].map(c => (
                        <div key={c} onClick={() => setNewMasterColor(c)} style={{ width: 22, height: 22, borderRadius: 5, background: c, cursor: 'pointer', border: newMasterColor === c ? '2px solid #111' : '2px solid transparent' }} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24, borderTop: `1px solid ${T.border}`, paddingTop: 16 }}>
                <button onClick={() => setShowAddMasterModal(false)} style={{ padding: '9px 20px', border: `1px solid ${T.border}`, borderRadius: 8, background: '#FFFFFF', color: '#2D3748', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                <button onClick={handleAddMasterItem} disabled={!newMasterValue.trim() || masterLoading || (masterTab === 'additional_sub_category' && !newMasterParent)}
                  style={{ padding: '9px 20px', border: 'none', borderRadius: 8, background: (newMasterValue.trim() && !(masterTab === 'additional_sub_category' && !newMasterParent)) ? T.active : '#C8CDD6', color: '#fff', fontWeight: 700, cursor: (newMasterValue.trim() && !(masterTab === 'additional_sub_category' && !newMasterParent)) ? 'pointer' : 'not-allowed', fontSize: 13 }}>
                  {masterLoading ? 'Adding...' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit master item modal */}
        {editingMasterItem && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#FFFFFF', borderRadius: 16, padding: 28, width: 420, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#1A202C' }}>Edit {MASTER_TYPES.find(t => t.key === masterTab)?.label || masterTab}</div>
                <button onClick={() => setEditingMasterItem(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}>
                  <Icon name="close" size={18} color={T.muted} />
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {masterTab === 'sub_category' && (
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Category</label>
                    <select value={editMasterParent} onChange={e => setEditMasterParent(e.target.value)}
                      style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit', background: '#fff' }}>
                      <option value=''>— No category —</option>
                      {(masterData['category'] || []).filter(c => c.is_active).map(c => (
                        <option key={c.id} value={c.value}>{c.value}</option>
                      ))}
                    </select>
                  </div>
                )}
                {masterTab === 'additional_sub_category' && (
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Sub-Category <span style={{ color: '#ef4444' }}>*</span></label>
                    <select value={editMasterParent} onChange={e => setEditMasterParent(e.target.value)}
                      style={{ width: '100%', boxSizing: 'border-box', border: `1.5px solid ${editMasterParent ? T.border : '#ef4444'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit', background: '#fff' }}>
                      <option value=''>— Select sub-category —</option>
                      {(masterData['sub_category'] || []).filter(c => c.is_active).map(c => (
                        <option key={c.id} value={c.value}>{c.value}{c.parent_value ? ` (${c.parent_value})` : ''}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Value <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    autoFocus
                    value={editMasterValue}
                    onChange={e => setEditMasterValue(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSaveMasterItem()}
                    style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Color</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input type="color" value={editMasterColor} onChange={e => setEditMasterColor(e.target.value)}
                      style={{ width: 48, height: 36, border: `1px solid ${T.border}`, borderRadius: 6, cursor: 'pointer', padding: 2 }} />
                    <span style={{ fontSize: 13, color: T.muted }}>{editMasterColor}</span>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {['#3B5BDB', '#2563eb', '#f59e0b', '#ef4444', '#8b5cf6', '#0891b2', '#dc2626', '#718096'].map(c => (
                        <div key={c} onClick={() => setEditMasterColor(c)} style={{ width: 22, height: 22, borderRadius: 5, background: c, cursor: 'pointer', border: editMasterColor === c ? '2px solid #111' : '2px solid transparent' }} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24, borderTop: `1px solid ${T.border}`, paddingTop: 16 }}>
                <button onClick={() => setEditingMasterItem(null)} style={{ padding: '9px 20px', border: `1px solid ${T.border}`, borderRadius: 8, background: '#FFFFFF', color: '#2D3748', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                <button onClick={handleSaveMasterItem} disabled={!editMasterValue.trim() || editMasterLoading || (masterTab === 'additional_sub_category' && !editMasterParent)}
                  style={{ padding: '9px 20px', border: 'none', borderRadius: 8, background: (editMasterValue.trim() && !(masterTab === 'additional_sub_category' && !editMasterParent)) ? T.active : '#C8CDD6', color: '#fff', fontWeight: 700, cursor: (editMasterValue.trim() && !(masterTab === 'additional_sub_category' && !editMasterParent)) ? 'pointer' : 'not-allowed', fontSize: 13 }}>
                  {editMasterLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // --- SLA Policies state ------------------------------------------------------
  const [slaPolicies, setSlaPolicies] = React.useState([]);
  const [slaLoading, setSlaLoading]   = React.useState(false);
  const [showSlaModal, setShowSlaModal] = React.useState(false);
  const [editingSla, setEditingSla]   = React.useState(null);
  const [slaForm, setSlaForm] = React.useState({
    name: '', description: '', is_default: false,
    low_response_hours: 48, medium_response_hours: 24, high_response_hours: 8, urgent_response_hours: 1,
    low_resolution_hours: 120, medium_resolution_hours: 72, high_resolution_hours: 24, urgent_resolution_hours: 4,
  });

  React.useEffect(() => {
    if (activeTab === 'SLA Policies') {
      setSlaLoading(true);
      slaAPI.list().then(setSlaPolicies).catch(() => {}).finally(() => setSlaLoading(false));
    }
  }, [activeTab]);

  const openSlaModal = (policy = null) => {
    setEditingSla(policy);
    if (policy) {
      setSlaForm({
        name: policy.name, description: policy.description || '', is_default: policy.is_default,
        low_response_hours: policy.low_response_hours, medium_response_hours: policy.medium_response_hours,
        high_response_hours: policy.high_response_hours, urgent_response_hours: policy.urgent_response_hours,
        low_resolution_hours: policy.low_resolution_hours, medium_resolution_hours: policy.medium_resolution_hours,
        high_resolution_hours: policy.high_resolution_hours, urgent_resolution_hours: policy.urgent_resolution_hours,
      });
    } else {
      setSlaForm({ name: '', description: '', is_default: false, low_response_hours: 48, medium_response_hours: 24, high_response_hours: 8, urgent_response_hours: 1, low_resolution_hours: 120, medium_resolution_hours: 72, high_resolution_hours: 24, urgent_resolution_hours: 4 });
    }
    setShowSlaModal(true);
  };

  const handleSaveSla = async () => {
    if (!slaForm.name.trim()) { toast('Name is required', 'warning'); return; }
    try {
      if (editingSla) {
        const updated = await slaAPI.update(editingSla.id, slaForm);
        setSlaPolicies(p => p.map(x => x.id === editingSla.id ? updated : x));
      } else {
        const created = await slaAPI.create(slaForm);
        setSlaPolicies(p => [...p, created]);
      }
      setShowSlaModal(false);
    } catch (err) { toast(err.message); }
  };

  const handleDeleteSla = async (id) => {
    if (!window.confirm('Delete this SLA policy?')) return;
    try {
      await slaAPI.delete(id);
      setSlaPolicies(p => p.filter(x => x.id !== id));
    } catch (err) { toast(err.message); }
  };

  const SLA_PRIORITIES = [
    { key: 'low',    label: 'Low',    color: '#718096' },
    { key: 'medium', label: 'Medium', color: '#f59e0b' },
    { key: 'high',   label: 'High',   color: '#f97316' },
    { key: 'urgent', label: 'Urgent', color: '#ef4444' },
  ];

  const renderSLAPolicies = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#1A202C' }}>SLA Policies</div>
          <div style={{ fontSize: 13, color: '#718096', marginTop: 3 }}>Define response and resolution time targets per priority level</div>
        </div>
        <button onClick={() => openSlaModal()} style={{ border: 'none', borderRadius: 8, padding: '9px 18px', background: T.active, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          + Add SLA Policy
        </button>
      </div>

      {slaLoading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#718096' }}>Loading...</div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {slaPolicies.map(policy => (
            <div key={policy.id} style={{ border: `1.5px solid ${policy.is_default ? T.active : T.border}`, borderRadius: 14, padding: '18px 20px', background: policy.is_default ? '#EBF0FF' : '#fff', position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: '#1A202C' }}>{policy.name}</span>
                  {policy.is_default && <span style={{ fontSize: 11, fontWeight: 700, background: T.active, color: '#fff', padding: '2px 8px', borderRadius: 99 }}>Default</span>}
                  {!policy.is_active && <span style={{ fontSize: 11, fontWeight: 700, background: '#F0F2F5', color: '#718096', padding: '2px 8px', borderRadius: 99 }}>Inactive</span>}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => openSlaModal(policy)} style={{ border: '1px solid #DDE1E7', borderRadius: 7, padding: '6px 14px', background: '#FFFFFF', fontSize: 12, fontWeight: 600, color: '#2D3748', cursor: 'pointer' }}>Edit</button>
                  {!policy.is_default && (
                    <button onClick={() => handleDeleteSla(policy.id)} style={{ border: '1px solid #fee2e2', borderRadius: 7, padding: '6px 14px', background: '#FFFFFF', fontSize: 12, fontWeight: 600, color: '#ef4444', cursor: 'pointer' }}>Delete</button>
                  )}
                </div>
              </div>
              {policy.description && <div style={{ fontSize: 12, color: '#718096', marginBottom: 14 }}>{policy.description}</div>}

              {/* SLA grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                {SLA_PRIORITIES.map(p => (
                  <div key={p.key} style={{ border: '1px solid #DDE1E7', borderRadius: 10, padding: '12px 14px', background: '#fafafa' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, display: 'inline-block' }} />
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#2D3748' }}>{p.label}</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#718096', marginBottom: 4 }}>
                      First Response
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1A202C', marginBottom: 8 }}>
                      {policy[`${p.key}_response_hours`]}h
                    </div>
                    <div style={{ fontSize: 11, color: '#718096', marginBottom: 4 }}>
                      Resolution
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1A202C' }}>
                      {policy[`${p.key}_resolution_hours`]}h
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* SLA Create/Edit Modal */}
      {showSlaModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ background: '#FFFFFF', borderRadius: 16, width: 640, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#1A202C' }}>{editingSla ? 'Edit SLA Policy' : 'New SLA Policy'}</div>
              <button onClick={() => setShowSlaModal(false)} style={{ border: 'none', background: 'transparent', fontSize: 22, color: '#718096', cursor: 'pointer', lineHeight: 1 }}>&#xD7;</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Policy Name *</label>
                <input value={slaForm.name} onChange={e => setSlaForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Standard SLA" style={{ width: '100%', border: '1px solid #C8CDD6', borderRadius: 8, padding: '9px 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Description</label>
                <textarea value={slaForm.description} onChange={e => setSlaForm(f => ({ ...f, description: e.target.value }))} rows={2} placeholder="Describe when this policy applies..." style={{ width: '100%', border: '1px solid #C8CDD6', borderRadius: 8, padding: '9px 12px', fontSize: 13, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500, color: '#2D3748' }}>
                <input type="checkbox" checked={slaForm.is_default} onChange={e => setSlaForm(f => ({ ...f, is_default: e.target.checked }))} />
                Set as default policy (applied to all new tickets)
              </label>

              <div style={{ borderTop: '1px solid #DDE1E7', paddingTop: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#2D3748', marginBottom: 14 }}>SLA Targets (in hours)</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                  {SLA_PRIORITIES.map(p => (
                    <div key={p.key} style={{ border: '1px solid #DDE1E7', borderRadius: 10, padding: '14px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, display: 'inline-block' }} />
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#2D3748' }}>{p.label}</span>
                      </div>
                      <label style={{ fontSize: 11, color: '#718096', display: 'block', marginBottom: 4 }}>First Response (hrs)</label>
                      <input type="number" min={1} value={slaForm[`${p.key}_response_hours`]} onChange={e => setSlaForm(f => ({ ...f, [`${p.key}_response_hours`]: parseInt(e.target.value) || 1 }))} style={{ width: '100%', border: '1px solid #C8CDD6', borderRadius: 6, padding: '6px 8px', fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 10 }} />
                      <label style={{ fontSize: 11, color: '#718096', display: 'block', marginBottom: 4 }}>Resolution (hrs)</label>
                      <input type="number" min={1} value={slaForm[`${p.key}_resolution_hours`]} onChange={e => setSlaForm(f => ({ ...f, [`${p.key}_resolution_hours`]: parseInt(e.target.value) || 1 }))} style={{ width: '100%', border: '1px solid #C8CDD6', borderRadius: 6, padding: '6px 8px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
              <button onClick={() => setShowSlaModal(false)} style={{ border: '1px solid #C8CDD6', borderRadius: 8, padding: '10px 20px', background: '#FFFFFF', fontWeight: 600, fontSize: 13, cursor: 'pointer', color: '#2D3748' }}>Cancel</button>
              <button onClick={handleSaveSla} style={{ border: 'none', borderRadius: 8, padding: '10px 24px', background: T.active, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                {editingSla ? 'Update Policy' : 'Create Policy'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ── Audit Log helpers ─────────────────────────────────────────────────────
  const auditActionBadge = (action) => {
    if (!action) return null;
    let bg, color;
    if (action.startsWith('login'))   { bg = '#eff6ff'; color = '#2563eb'; }
    else if (action.startsWith('ticket')) { bg = '#f0fdf4'; color = '#16a34a'; }
    else if (action.startsWith('user'))   { bg = '#fdf4ff'; color = '#9333ea'; }
    else if (action.startsWith('group'))  { bg = '#fff7ed'; color = '#ea580c'; }
    else                                  { bg = '#f1f5f9'; color = '#64748b'; }
    return (
      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: bg, color }}>
        {action}
      </span>
    );
  };

  const renderAuditLogs = () => {
    const auditPages = Math.max(1, Math.ceil(auditTotal / AUDIT_LIMIT));

    const inputStyle = {
      height: 34, border: `1.5px solid ${T.border}`, borderRadius: 8, padding: '0 10px',
      fontSize: 13, background: '#fff', color: '#1A202C', outline: 'none',
      fontFamily: 'inherit',
    };
    const selectStyle = { ...inputStyle, cursor: 'pointer' };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 16 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1A202C' }}>Audit Logs</div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>Track all significant actions across the system</div>
          </div>
          <button
            type="button"
            onClick={async () => {
              try {
                const token = localStorage.getItem('token');
                const url = auditAPI.exportUrl({
                  ...(auditSearch     ? { search:      auditSearch }     : {}),
                  ...(auditAction !== 'all' ? { action: auditAction }    : {}),
                  ...(auditEntityType !== 'all' ? { entity_type: auditEntityType } : {}),
                  ...(auditDateFrom   ? { date_from:   auditDateFrom }   : {}),
                  ...(auditDateTo     ? { date_to:     auditDateTo }     : {}),
                });
                const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                if (!resp.ok) throw new Error('Export failed');
                const blob = await resp.blob();
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `audit_logs_${Date.now()}.csv`;
                a.click();
                URL.revokeObjectURL(a.href);
              } catch (err) {
                toast('Export failed: ' + err.message);
              }
            }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 14px', borderRadius: 8, background: T.active, color: '#fff', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
          >
            <Icon name="download" size={14} color="#fff" />
            Export CSV
          </button>
        </div>

        {/* Filter bar */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <input
            style={{ ...inputStyle, width: 220 }}
            placeholder="Search user, description…"
            value={auditSearch}
            onChange={e => setAuditSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && fetchAuditLogs(1)}
          />
          <select style={{ ...selectStyle, width: 160 }} value={auditAction} onChange={e => { setAuditAction(e.target.value); }}>
            <option value="all">All Actions</option>
            <option value="login_success">login_success</option>
            <option value="login_failed">login_failed</option>
            <option value="ticket.created">ticket.created</option>
            <option value="ticket.status_changed">ticket.status_changed</option>
            <option value="ticket.priority_changed">ticket.priority_changed</option>
            <option value="ticket.assigned">ticket.assigned</option>
            <option value="ticket.group_changed">ticket.group_changed</option>
            <option value="ticket.replied">ticket.replied</option>
            <option value="ticket.note_added">ticket.note_added</option>
            <option value="ticket.forwarded">ticket.forwarded</option>
            <option value="user.created">user.created</option>
            <option value="user.updated">user.updated</option>
            <option value="user.role_changed">user.role_changed</option>
            <option value="user.deactivated">user.deactivated</option>
            <option value="group.created">group.created</option>
            <option value="group.updated">group.updated</option>
            <option value="group.deleted">group.deleted</option>
          </select>
          <select style={{ ...selectStyle, width: 140 }} value={auditEntityType} onChange={e => { setAuditEntityType(e.target.value); }}>
            <option value="all">All Entities</option>
            <option value="auth">auth</option>
            <option value="ticket">ticket</option>
            <option value="user">user</option>
            <option value="group">group</option>
          </select>
          <input type="date" style={{ ...inputStyle, width: 140 }} value={auditDateFrom} onChange={e => setAuditDateFrom(e.target.value)} title="From date" />
          <input type="date" style={{ ...inputStyle, width: 140 }} value={auditDateTo} onChange={e => setAuditDateTo(e.target.value)} title="To date" />
          <button
            type="button"
            onClick={() => fetchAuditLogs(1)}
            style={{ height: 34, padding: '0 16px', borderRadius: 8, background: T.active, color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            Apply
          </button>
          <button
            type="button"
            onClick={() => { setAuditSearch(''); setAuditAction('all'); setAuditEntityType('all'); setAuditDateFrom(''); setAuditDateTo(''); setTimeout(() => fetchAuditLogs(1), 0); }}
            style={{ height: 34, padding: '0 14px', borderRadius: 8, background: 'transparent', color: T.muted, border: `1.5px solid ${T.border}`, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            Clear
          </button>
        </div>

        {/* Table */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', border: `1px solid ${T.border}`, borderRadius: 10 }}>
          {auditLoading ? (
            <div style={{ padding: 40, textAlign: 'center', color: T.muted, fontSize: 13 }}>Loading…</div>
          ) : auditLogs.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: T.muted, fontSize: 13 }}>No audit log entries found.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: T.g50, position: 'sticky', top: 0, zIndex: 1 }}>
                  {['Date / Time', 'User', 'Role', 'Action', 'Description', 'IP Address'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', fontWeight: 700, color: T.muted, textAlign: 'left', borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {auditLogs.map((log, i) => (
                  <tr key={log.id} style={{ background: i % 2 === 0 ? '#fff' : T.g50, borderBottom: `1px solid ${T.border}` }}>
                    <td style={{ padding: '9px 12px', whiteSpace: 'nowrap', color: T.muted }}>
                      {log.created_at ? formatInTz(log.created_at, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                    <td style={{ padding: '9px 12px' }}>
                      <div style={{ fontWeight: 600, color: '#1A202C' }}>{log.user_name || '—'}</div>
                      {log.user_email && <div style={{ fontSize: 11, color: T.muted }}>{log.user_email}</div>}
                    </td>
                    <td style={{ padding: '9px 12px', color: T.muted, whiteSpace: 'nowrap' }}>{log.user_role || '—'}</td>
                    <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>{auditActionBadge(log.action)}</td>
                    <td style={{ padding: '9px 12px', color: '#1A202C', maxWidth: 300 }}>{log.description || '—'}</td>
                    <td style={{ padding: '9px 12px', color: T.muted, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>{log.ip_address || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!auditLoading && auditPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end', fontSize: 13 }}>
            <span style={{ color: T.muted }}>
              {((auditPage - 1) * AUDIT_LIMIT) + 1}–{Math.min(auditPage * AUDIT_LIMIT, auditTotal)} of {auditTotal}
            </span>
            <button
              type="button"
              disabled={auditPage <= 1}
              onClick={() => fetchAuditLogs(auditPage - 1)}
              style={{ height: 30, padding: '0 12px', borderRadius: 7, border: `1.5px solid ${T.border}`, background: auditPage <= 1 ? T.g50 : '#fff', cursor: auditPage <= 1 ? 'default' : 'pointer', fontSize: 13, color: auditPage <= 1 ? T.muted : '#1A202C', fontFamily: 'inherit' }}
            >Prev</button>
            <span style={{ color: '#1A202C', fontWeight: 600 }}>{auditPage} / {auditPages}</span>
            <button
              type="button"
              disabled={auditPage >= auditPages}
              onClick={() => fetchAuditLogs(auditPage + 1)}
              style={{ height: 30, padding: '0 12px', borderRadius: 7, border: `1.5px solid ${T.border}`, background: auditPage >= auditPages ? T.g50 : '#fff', cursor: auditPage >= auditPages ? 'default' : 'pointer', fontSize: 13, color: auditPage >= auditPages ? T.muted : '#1A202C', fontFamily: 'inherit' }}
            >Next</button>
          </div>
        )}
      </div>
    );
  };

  // ── Timezone settings ─────────────────────────────────────────────────────
  const [tzSelected, setTzSelected]   = React.useState(getAppTimezone());
  const [tzSearch, setTzSearch]       = React.useState('');
  const [tzSaved, setTzSaved]         = React.useState(false);
  const [tzNowTick, setTzNowTick]     = React.useState(Date.now());
  React.useEffect(() => {
    if (activeTab !== 'Timezone') return;
    const id = setInterval(() => setTzNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [activeTab]);
  const renderTimezone = () => {
    const browserTz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch (_) { return 'UTC'; } })();
    const all = listAllTimezones();
    const curatedSet = new Set(COMMON_TIMEZONES.map(t => t.value));
    const labelFor = v => {
      const c = COMMON_TIMEZONES.find(t => t.value === v);
      if (c) return c.label;
      try {
        const parts = new Intl.DateTimeFormat('en-GB', { timeZone: v, timeZoneName: 'shortOffset' }).formatToParts(new Date());
        const off = (parts.find(p => p.type === 'timeZoneName') || {}).value || '';
        return `(${off}) ${v.replace(/_/g, ' ')}`;
      } catch (_) {
        return v;
      }
    };
    const filtered = all
      .filter(v => !tzSearch.trim() || v.toLowerCase().includes(tzSearch.toLowerCase()) || labelFor(v).toLowerCase().includes(tzSearch.toLowerCase()))
      .sort((a, b) => {
        const aC = curatedSet.has(a), bC = curatedSet.has(b);
        if (aC && !bC) return -1;
        if (!aC && bC) return 1;
        return a.localeCompare(b);
      });
    const onSave = () => {
      setAppTimezone(tzSelected);
      setTzSaved(true);
      setTimeout(() => setTzSaved(false), 2200);
    };
    const onReset = () => {
      setTzSelected(browserTz);
      setAppTimezone(null);
      setTzSaved(true);
      setTimeout(() => setTzSaved(false), 2200);
    };
    const previewNow = new Date(tzNowTick);
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#1A202C' }}>Timezone</div>
            <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
              Choose the time zone used to display ticket dates, SLA timers and reports across your workspace.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onReset}
              style={{ height: 36, padding: '0 14px', borderRadius: 8, border: `1.5px solid ${T.border}`, background: '#fff', cursor: 'pointer', fontSize: 13, color: '#1A202C', fontWeight: 600, fontFamily: 'inherit' }}>
              Use browser default
            </button>
            <button onClick={onSave}
              style={{ height: 36, padding: '0 18px', borderRadius: 8, border: 'none', background: T.active, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>
              Save changes
            </button>
          </div>
        </div>

        {tzSaved && (
          <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', color: '#065F46', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
            Time zone preference saved. New dates will now display in <b>{getAppTimezone()}</b>.
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: 16 }}>
          {/* Selector card */}
          <div style={{ background: '#FFFFFF', border: `1px solid ${T.border}`, borderRadius: 14, padding: 18 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1A202C', marginBottom: 4 }}>Workspace time zone</div>
            <div style={{ fontSize: 12, color: T.muted, marginBottom: 14 }}>
              Detected from your browser: <b>{browserTz}</b>
            </div>

            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 6 }}>Search</label>
            <input
              type="text"
              value={tzSearch}
              onChange={e => setTzSearch(e.target.value)}
              placeholder="Search by city or region (e.g. Singapore, GMT+8)..."
              style={{ width: '100%', height: 38, padding: '0 12px', borderRadius: 8, border: `1.5px solid ${T.border}`, fontSize: 13, fontFamily: 'inherit', marginBottom: 12, outline: 'none', boxSizing: 'border-box' }}
            />

            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 6 }}>Time zone</label>
            <select
              value={tzSelected}
              onChange={e => setTzSelected(e.target.value)}
              size={12}
              style={{ width: '100%', padding: '6px 10px', borderRadius: 8, border: `1.5px solid ${T.border}`, fontSize: 13, fontFamily: 'inherit', outline: 'none', background: '#fff' }}
            >
              {filtered.map(v => (
                <option key={v} value={v}>{labelFor(v)}</option>
              ))}
            </select>
            <div style={{ fontSize: 11, color: T.muted, marginTop: 8 }}>
              Showing {filtered.length} of {all.length} time zones
            </div>
          </div>

          {/* Live preview card */}
          <div style={{ background: '#FFFFFF', border: `1px solid ${T.border}`, borderRadius: 14, padding: 18, display: 'flex', flexDirection: 'column', gap: 14, height: 'fit-content' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1A202C' }}>Live preview</div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Selected zone</div>
              <div style={{ fontSize: 13, color: '#1A202C', wordBreak: 'break-all' }}>{tzSelected}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Current time</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: T.active, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                {previewNow.toLocaleString('en-GB', { timeZone: tzSelected, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}
              </div>
              <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
                {previewNow.toLocaleString('en-GB', { timeZone: tzSelected, weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
              </div>
            </div>
            <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Sample ticket date</div>
              <div style={{ fontSize: 12, color: '#475569' }}>
                {previewNow.toLocaleString('en-GB', { timeZone: tzSelected, weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true })}
              </div>
              <div style={{ fontSize: 11, color: T.muted, marginTop: 6 }}>
                Currently active: <b>{getAppTimezone()}</b>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  // ── Priority Keywords ──────────────────────────────────────────────────────
  const [pkList, setPkList]         = React.useState([]);
  const [pkLoading, setPkLoading]   = React.useState(false);
  const [pkNewKw, setPkNewKw]       = React.useState('');
  const [pkNewPri, setPkNewPri]     = React.useState('Urgent');
  const [pkSaving, setPkSaving]     = React.useState(false);

  React.useEffect(() => {
    if (activeTab !== 'Priority Keywords') return;
    setPkLoading(true);
    priorityKeywordsAPI.list()
      .then(data => setPkList(data || []))
      .catch(() => {})
      .finally(() => setPkLoading(false));
  }, [activeTab]);

  const PRIORITY_META = {
    Urgent: { color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' },
    High:   { color: '#d97706', bg: '#fffbeb', border: '#fcd34d' },
    Medium: { color: '#2563eb', bg: '#eff6ff', border: '#93c5fd' },
    Low:    { color: '#16a34a', bg: '#f0fdf4', border: '#86efac' },
  };

  const renderPriorityKeywords = () => {
    const grouped = { Urgent: [], High: [], Medium: [], Low: [] };
    pkList.forEach(k => { if (grouped[k.priority]) grouped[k.priority].push(k); });

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* Header */}
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#1A202C' }}>Priority Keywords</div>
          <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
            When a ticket arrives via email, its subject &amp; body are scanned for these keywords to auto-set the priority.
            Higher priority levels are checked first (Urgent → High → Medium → Low). Default is <strong>Medium</strong> if no keyword matches.
          </div>
        </div>

        {/* Add keyword */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', background: T.g50, border: `1px solid ${T.border}`, borderRadius: 12, padding: '14px 18px' }}>
          <select
            value={pkNewPri}
            onChange={e => setPkNewPri(e.target.value)}
            style={{ height: 38, border: `1px solid ${T.border}`, borderRadius: 8, padding: '0 10px', fontSize: 13, fontWeight: 600, color: PRIORITY_META[pkNewPri].color, background: PRIORITY_META[pkNewPri].bg, cursor: 'pointer' }}
          >
            {['Urgent', 'High', 'Medium', 'Low'].map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Type a keyword, e.g. outage"
            value={pkNewKw}
            onChange={e => setPkNewKw(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !pkSaving && pkNewKw.trim() && handleAddKeyword()}
            style={{ flex: 1, minWidth: 200, height: 38, border: `1px solid ${T.border}`, borderRadius: 8, padding: '0 12px', fontSize: 13 }}
          />
          <button
            disabled={pkSaving || !pkNewKw.trim()}
            onClick={handleAddKeyword}
            style={{ height: 38, padding: '0 18px', background: 'linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%)', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, fontSize: 13, cursor: pkSaving || !pkNewKw.trim() ? 'not-allowed' : 'pointer', opacity: pkNewKw.trim() ? 1 : 0.5 }}
          >
            {pkSaving ? 'Adding…' : '+ Add Keyword'}
          </button>
        </div>

        {/* Keyword cards per priority */}
        {pkLoading ? (
          <div style={{ color: T.muted, fontSize: 13, textAlign: 'center', padding: 32 }}>Loading…</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {['Urgent', 'High', 'Medium', 'Low'].map(pri => {
              const m = PRIORITY_META[pri];
              const kws = grouped[pri];
              return (
                <div key={pri} style={{ border: `1.5px solid ${m.border}`, borderRadius: 14, overflow: 'hidden' }}>
                  <div style={{ background: m.bg, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: m.color, display: 'inline-block', flexShrink: 0 }} />
                    <span style={{ fontWeight: 700, fontSize: 14, color: m.color }}>{pri} Priority</span>
                    <span style={{ marginLeft: 'auto', fontSize: 12, color: m.color, fontWeight: 600, background: 'rgba(0,0,0,0.06)', borderRadius: 99, padding: '2px 8px' }}>{kws.length}</span>
                  </div>
                  <div style={{ padding: '12px 14px', display: 'flex', flexWrap: 'wrap', gap: 8, minHeight: 56, background: '#FFFFFF' }}>
                    {kws.length === 0 && (
                      <span style={{ fontSize: 12, color: T.muted, fontStyle: 'italic' }}>No keywords yet</span>
                    )}
                    {kws.map(k => (
                      <span
                        key={k.id}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: m.bg, border: `1px solid ${m.border}`, color: m.color, borderRadius: 99, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}
                      >
                        {k.keyword}
                        <button
                          onClick={() => handleDeleteKeyword(k.id)}
                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: m.color, display: 'flex', alignItems: 'center', padding: 0, opacity: 0.7, lineHeight: 1 }}
                          title="Remove"
                        >×</button>
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  async function handleAddKeyword() {
    if (!pkNewKw.trim() || pkSaving) return;
    setPkSaving(true);
    try {
      const created = await priorityKeywordsAPI.create({ priority: pkNewPri, keyword: pkNewKw.trim() });
      setPkList(prev => [...prev, created]);
      setPkNewKw('');
      toast(`Keyword "${created.keyword}" added to ${pkNewPri}`, 'success');
    } catch (e) {
      toast(e.message || 'Failed to add keyword', 'error');
    }
    setPkSaving(false);
  }

  async function handleDeleteKeyword(id) {
    try {
      await priorityKeywordsAPI.delete(id);
      setPkList(prev => prev.filter(k => k.id !== id));
    } catch (e) {
      toast(e.message || 'Failed to delete keyword', 'error');
    }
  }

  const renderContent = () => {
    if (activeTab === 'Groups' || activeTab === 'Agent Groups') return renderGroups();
    if (activeTab === 'Users') return renderUsers('requesters');
    if (activeTab === 'Agents' || activeTab === 'Agent Users') return renderUsers('agents');
    if (activeTab === 'Requesters') return renderUsers('requesters');
    if (activeTab === 'Requester Groups') return renderRequesterGroups();
    if (activeTab === 'Master Data') return renderMasterData();
    if (activeTab === 'SLA Policies') return <SLAAdmin />;
    if (activeTab === 'Pause Requests') return <PauseRequestsAdmin />;
    if (activeTab === 'Attendance') return <AttendancePage mode="self" />;
    if (activeTab === 'Attendance Settings') return <AttendancePage mode="settings" />;
    if (activeTab === 'Email Notifications') return renderEmailNotifications();
    if (activeTab === 'Timezone') return renderTimezone();
    if (activeTab === 'Workspace Settings') return renderWorkspaceSettings();
    if (activeTab === 'Audit Logs') return renderAuditLogs();
    if (activeTab === 'Priority Keywords') return renderPriorityKeywords();
    if (activeTab === 'Knowledge Base' || activeTab === 'FAQ') return <FAQAdmin />;
    if (activeTab === 'Reset') return <ResetAdmin />;
    if (activeTab === 'M365 Automation') return <AutomationAdmin />;
    if (activeTab === 'M365 User') return <M365UserRegistryAdmin />;
    if (activeTab === 'M365 Group') return <M365GroupRegistryAdmin />;
    if (activeTab === 'M365 Users') return <M365UsersAdmin />;
    if (activeTab === 'M365 Groups') return <M365GroupsAdmin />;
    return null;
  };

  // Reset template editor when leaving Email Notifications tab
  useEffect(() => {
    if (activeTab !== 'Email Notifications') setEditingTemplate(null);
  }, [activeTab]);

  const renderHelpPanel = () => {
    if (activeTab === 'Groups') {
      return (
        <div style={{ background: '#FFFFFF', border: `1px solid ${T.border}`, borderRadius: 18, padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#1A202C' }}>Groups</div>
          <div style={{ fontSize: 13, lineHeight: 1.6, color: T.muted }}>
            You can organize your agents into specific Groups like �Sales� and �Product Management�. Segmenting them into divisions lets you easily assign tickets, create specific canned responses, manage workflows and generate group-level reports. Note that the same agent can be a member of multiple groups as well.
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Auto-ticket Assignment</div>
            <div style={{ fontSize: 13, lineHeight: 1.6, color: T.muted }}>
              Once you create homogeneous agent groups, you can choose to automatically assign new tickets in this group to the next agent in Round Robin.
            </div>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Working Hours</div>
            <div style={{ fontSize: 13, lineHeight: 1.6, color: T.muted }}>
              You can assign a different set of business hours and holidays to each Group. For example, create separate groups for each time zone or support shift.
            </div>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Restricted group</div>
            <div style={{ fontSize: 13, lineHeight: 1.6, color: T.muted }}>
              If a group is marked as restricted, only agents who are part of it can see tickets assigned to it. Agents with access set to �all groups� will not see restricted group tickets unless added as a member.
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === 'Email Notifications') {
      return (
        <div style={{ background: '#FFFFFF', border: `1px solid ${T.border}`, borderRadius: 18, padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#1A202C' }}>Email Notifications</div>
          <div style={{ fontSize: 13, lineHeight: 1.6, color: T.muted }}>
            Toggle <strong>✓ / ✕</strong> for who receives mail. Click <strong>✎</strong> under <strong>Requester</strong> (or Agent, etc.) to edit that role&apos;s template — use placeholders like <code>{'{{requester_name}}'}</code> so each email is dynamic.
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.6, color: T.muted }}>
            You can configure automatic email notifications to agents and requesters when specific events occur. For example, notify users when an agent comments on their request or when SLA is breached.
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.6, color: T.muted }}>
            Send email notifications instantly for activities across tickets, problems, changes and releases. Enable or disable any notification from this page except important system alerts.
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.6, color: T.muted }}>
            Customize email templates for agent replies and add dynamic content in replies using placeholders.
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.6, color: T.muted }}>
            If your plan supports multiple languages, create customized email notification templates for each language.
          </div>
          <button type="button" style={{ color: T.active, fontSize: 13, fontWeight: 700, textDecoration: 'none', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit' }}>Click here to learn more</button>
        </div>
      );
    }

    if (activeTab === 'Timezone') return null;

    return (
      <div style={{ background: '#FFFFFF', border: `1px solid ${T.border}`, borderRadius: 18, padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#1A202C' }}>{activeTab} help</div>
        <div style={{ fontSize: 13, lineHeight: 1.6, color: T.muted }}>Manage this section from the admin panel.</div>
      </div>
    );
  };

  const agentNamesToMemberIds = (names) => (names || [])
    .map(name => users.find(u => u.name === name)?.id)
    .filter(id => id != null);

  const reloadGroups = async () => {
    const data = await groupsAPI.list();
    setGroups((data || []).map(g => ({
      id: g.id,
      name: g.name || '',
      description: g.description || '',
      members: g.members || [],
    })));
  };

  const handleSaveEditGroup = async () => {
    if (!editingGroup || !editGroupName.trim()) return;
    try {
      const member_ids = agentNamesToMemberIds(editGroupAgents);
      await groupsAPI.update(editingGroup.id, {
        name: editGroupName.trim(),
        description: editGroupDesc.trim() || 'Agent group membership and permissions.',
        member_ids,
      });
      await reloadGroups();
      toast('Agent group updated', 'success');
    } catch (err) {
      toast('Failed to update group: ' + err.message, 'error');
      return;
    }
    setEditingGroup(null);
    setEditGroupName('');
    setEditGroupDesc('');
    setEditGroupAgents([]);
    setEditAgentSearch('');
    setEditAgentDropdownOpen(false);
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      const member_ids = agentNamesToMemberIds(selectedAgents);
      await groupsAPI.create({
        name: newGroupName.trim(),
        description: newGroupDesc.trim() || 'Agent group membership and permissions.',
        member_ids,
      });
      await reloadGroups();
      toast('Agent group created', 'success');
    } catch (err) {
      toast('Failed to create group: ' + err.message, 'error');
      return;
    }
    setShowCreateGroupModal(false);
    setNewGroupName('');
    setNewGroupDesc('');
    setSelectedAgents([]);
    setAgentSearch('');
    setAgentDropdownOpen(false);
  };

  return (
    <div style={{ flex: 1, padding: '20px 24px', overflowY: (['Groups', 'Users', 'Agent Users', 'Master Data', 'SLA Policies', 'Audit Logs', 'Pause Requests', 'Requester Groups'].includes(activeTab)) ? 'hidden' : 'auto', background: T.bg, display: 'flex', gap: 20, minHeight: 0 }}>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ background: T.txt, border: `1px solid ${T.border}`, borderRadius: 18, padding: 20, display: 'flex', flexDirection: 'column', ...(['Groups', 'Users', 'Agent Users', 'Master Data', 'SLA Policies', 'Audit Logs', 'Pause Requests', 'Requester Groups'].includes(activeTab) ? { flex: 1, minHeight: 0, overflow: 'hidden' } : { minHeight: 520 }) }}>
          {renderContent()}
        </div>
      </div>

      {!['Groups', 'Users', 'Agents', 'Agent Users', 'Requesters', 'Agent Groups', 'Requester Groups', 'Master Data', 'SLA Policies', 'Priority Keywords', 'Email Notifications', 'Audit Logs', 'Pause Requests', 'Attendance', 'Attendance Settings', 'Workspace Settings', 'Timezone', 'Knowledge Base', 'FAQ', 'Reset', 'M365 Automation', 'M365 User', 'M365 Group', 'M365 Users', 'M365 Groups'].includes(activeTab) && (
        <aside style={{ width: 320, minWidth: 320, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {renderHelpPanel()}
        </aside>
      )}

      {showCreateReqGroupModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#FFFFFF', borderRadius: 16, padding: 28, width: 560, maxWidth: '95vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#1A202C' }}>{editingReqGroup ? 'Edit' : 'Create'} Requester Group</div>
              <button onClick={() => setShowCreateReqGroupModal(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}>
                <Icon name="close" size={18} color={T.muted} />
              </button>
            </div>

            <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#2D3748', marginBottom: 6 }}>Group Name *</label>
                <input
                  type="text"
                  value={reqGroupName}
                  onChange={e => setReqGroupName(e.target.value)}
                  placeholder="e.g. Finance Team"
                  style={{ width: '100%', padding: '10px 12px', border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#2D3748', marginBottom: 6 }}>Description</label>
                <textarea
                  value={reqGroupDesc}
                  onChange={e => setReqGroupDesc(e.target.value)}
                  rows={3}
                  placeholder="Optional description"
                  style={{ width: '100%', padding: '10px 12px', border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#2D3748', marginBottom: 6 }}>Members (Requesters)</label>
                <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, maxHeight: 240, overflowY: 'auto', padding: 8 }}>
                  {users.filter(u => u.rawRole === 'requester').length === 0 && (
                    <div style={{ color: T.muted, fontSize: 12, padding: 8 }}>No requesters available. Add requesters first.</div>
                  )}
                  {users.filter(u => u.rawRole === 'requester').map(u => {
                    const checked = reqGroupMemberIds.includes(u.id);
                    return (
                      <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', cursor: 'pointer', borderRadius: 6 }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={e => {
                            if (e.target.checked) setReqGroupMemberIds(prev => [...prev, u.id]);
                            else setReqGroupMemberIds(prev => prev.filter(id => id !== u.id));
                          }}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#1A202C' }}>{u.name}</span>
                          <span style={{ fontSize: 11, color: T.muted }}>{u.email}</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, paddingTop: 16, borderTop: `1px solid ${T.border}` }}>
              <button
                onClick={() => setShowCreateReqGroupModal(false)}
                style={{ padding: '10px 18px', border: `1px solid ${T.border}`, background: '#FFFFFF', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#2D3748' }}
              >Cancel</button>
              <button
                onClick={handleSaveReqGroup}
                style={{ padding: '10px 18px', border: 'none', background: 'linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%)', color: '#FFFFFF', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >{editingReqGroup ? 'Save Changes' : 'Create Group'}</button>
            </div>
          </div>
        </div>
      )}

      {showCreateGroupModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#FFFFFF', borderRadius: 16, padding: 28, width: 520, maxWidth: '95vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#1A202C' }}>Create Agent Group</div>
              <button onClick={() => setShowCreateGroupModal(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}>
                <Icon name="close" size={18} color={T.muted} />
              </button>
            </div>

            <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Group Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  value={newGroupName}
                  onChange={e => setNewGroupName(e.target.value)}
                  placeholder="e.g. Customer Success Team"
                  style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
                />
              </div>

              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Description</label>
                <textarea
                  value={newGroupDesc}
                  onChange={e => setNewGroupDesc(e.target.value)}
                  placeholder="Brief description of this group..."
                  rows={2}
                  style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, resize: 'vertical', outline: 'none', fontFamily: 'inherit' }}
                />
              </div>

              {/* Add Agents dropdown */}
              <div style={{ position: 'relative' }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Add Agents</label>
                <div
                  onClick={() => setAgentDropdownOpen(o => !o)}
                  style={{ border: `1px solid ${agentDropdownOpen ? T.active : T.border}`, borderRadius: 8, padding: '8px 10px', cursor: 'pointer', display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', minHeight: 42, background: '#FFFFFF', boxSizing: 'border-box' }}
                >
                  {selectedAgents.length === 0 && <span style={{ color: '#718096', fontSize: 13 }}>Select agents...</span>}
                  {selectedAgents.map(agent => (
                    <span key={agent} style={{ background: '#EBF0FF', color: T.active, border: `1px solid #C7D2FE`, borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {agent.split(' ')[0]}
                      <button
                        type="button"
                        onMouseDown={e => { e.stopPropagation(); setSelectedAgents(prev => prev.filter(a => a !== agent)); }}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: T.active, padding: 0, fontSize: 14, lineHeight: 1, display: 'flex', alignItems: 'center' }}
                      >�</button>
                    </span>
                  ))}
                  <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <Icon name={agentDropdownOpen ? 'chevronU' : 'chevronD'} size={13} color={T.muted} />
                  </span>
                </div>

                {agentDropdownOpen && (
                  <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 1001 }} onClick={() => setAgentDropdownOpen(false)} />
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 1002, background: '#FFFFFF', border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: 240 }}>
                      <div style={{ padding: '8px 10px', borderBottom: `1px solid ${T.border}` }}>
                        <input
                          autoFocus
                          value={agentSearch}
                          onChange={e => setAgentSearch(e.target.value)}
                          placeholder="Search agents..."
                          onClick={e => e.stopPropagation()}
                          style={{ width: '100%', border: 'none', outline: 'none', fontSize: 13, fontFamily: 'inherit', background: 'transparent' }}
                        />
                      </div>
                      <div style={{ overflowY: 'auto', flex: 1 }}>
                        {allAgents.filter(a => a.toLowerCase().includes(agentSearch.toLowerCase())).map(agent => {
                          const initials = agent.split(' ').map(n => n[0]).join('').slice(0, 2);
                          const checked = selectedAgents.includes(agent);
                          return (
                            <label key={agent} onMouseDown={e => e.preventDefault()} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer', background: checked ? '#EBF0FF' : '#fff', borderBottom: `1px solid ${T.border}` }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => setSelectedAgents(prev => checked ? prev.filter(a => a !== agent) : [...prev, agent])}
                                style={{ accentColor: T.active, width: 15, height: 15, flexShrink: 0 }}
                              />
                              <div style={{ width: 28, height: 28, borderRadius: 7, background: T.g50, display: 'grid', placeItems: 'center', color: T.active, fontWeight: 800, fontSize: 11, flexShrink: 0 }}>{initials}</div>
                              <span style={{ fontSize: 13, fontWeight: 600, color: '#1A202C' }}>{agent}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
                {selectedAgents.length > 0 && <div style={{ fontSize: 12, color: T.active, marginTop: 5, fontWeight: 600 }}>{selectedAgents.length} agent{selectedAgents.length !== 1 ? 's' : ''} selected</div>}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, borderTop: `1px solid ${T.border}`, paddingTop: 16 }}>
              <button onClick={() => setShowCreateGroupModal(false)} style={{ padding: '9px 20px', border: `1px solid ${T.border}`, borderRadius: 8, background: '#FFFFFF', color: '#2D3748', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button
                onClick={handleCreateGroup}
                disabled={!newGroupName.trim()}
                style={{ padding: '9px 20px', border: 'none', borderRadius: 8, background: newGroupName.trim() ? T.active : '#C8CDD6', color: '#fff', fontWeight: 700, cursor: newGroupName.trim() ? 'pointer' : 'not-allowed', fontSize: 13 }}
              >
                Create Group
              </button>
            </div>
          </div>
        </div>
      )}

      {viewingUser && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#FFFFFF', borderRadius: 16, padding: 28, width: 420, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#1A202C' }}>User Details</div>
              <button onClick={() => setViewingUser(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}>
                <Icon name="close" size={18} color={T.muted} />
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, paddingBottom: 20, borderBottom: `1px solid ${T.border}` }}>
              <div style={{ width: 56, height: 56, borderRadius: 14, background: T.g50, display: 'grid', placeItems: 'center', color: T.active, fontWeight: 800, fontSize: 18, flexShrink: 0 }}>
                {viewingUser.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize: 17, fontWeight: 700, color: '#1A202C' }}>{viewingUser.name}</div>
                <div style={{ fontSize: 13, color: T.muted, marginTop: 2 }}>{viewingUser.role || 'Agent'}</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', width: 100, flexShrink: 0 }}>Email</span>
                <span style={{ fontSize: 13, color: '#1A202C' }}>{viewingUser.email}</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', width: 100, flexShrink: 0 }}>Role</span>
                <span style={{ fontSize: 13, color: '#1A202C' }}>{viewingUser.role || 'Agent'}</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', width: 100, flexShrink: 0 }}>Last Active</span>
                <span style={{ fontSize: 13, color: '#1A202C' }}>{viewingUser.lastActive}</span>
              </div>
            </div>
            <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setViewingUser(null)} style={{ padding: '9px 20px', border: `1px solid ${T.border}`, borderRadius: 8, background: '#FFFFFF', color: '#2D3748', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {editingUser && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#FFFFFF', borderRadius: 16, padding: 28, width: 460, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#1A202C' }}>Edit User</div>
              <button onClick={() => setEditingUser(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}>
                <Icon name="close" size={18} color={T.muted} />
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Full Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  value={editUserName}
                  onChange={e => setEditUserName(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Email <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  value={editUserEmail}
                  onChange={e => setEditUserEmail(e.target.value)}
                  type="email"
                  style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Role</label>
                <select
                  value={editUserRole}
                  onChange={e => setEditUserRole(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit', background: '#FFFFFF' }}
                >
                  <option value="">Select a role...</option>
                  <option>Agent</option>
                  <option>Senior Agent</option>
                  <option>Team Lead</option>
                  <option>Supervisor</option>
                  <option>Manager</option>
                  <option>IT Admin</option>
                  <option>Support Engineer</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24, borderTop: `1px solid ${T.border}`, paddingTop: 16 }}>
              <button onClick={() => setEditingUser(null)} style={{ padding: '9px 20px', border: `1px solid ${T.border}`, borderRadius: 8, background: '#FFFFFF', color: '#2D3748', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button
                onClick={handleSaveEditUser}
                disabled={!editUserName.trim() || !editUserEmail.trim()}
                style={{ padding: '9px 20px', border: 'none', borderRadius: 8, background: (editUserName.trim() && editUserEmail.trim()) ? T.active : '#C8CDD6', color: '#fff', fontWeight: 700, cursor: (editUserName.trim() && editUserEmail.trim()) ? 'pointer' : 'not-allowed', fontSize: 13 }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddRequesterModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#FFFFFF', borderRadius: 16, padding: 28, width: 460, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#1A202C' }}>Add Requester</div>
              <button onClick={() => setShowAddRequesterModal(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}>
                <Icon name="close" size={18} color={T.muted} />
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Full Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  value={newRequesterName}
                  onChange={e => setNewRequesterName(e.target.value)}
                  placeholder="e.g. John Smith"
                  style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Email <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  value={newRequesterEmail}
                  onChange={e => setNewRequesterEmail(e.target.value)}
                  placeholder="e.g. john.smith@company.com"
                  type="email"
                  style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Phone</label>
                  <input
                    value={newRequesterPhone}
                    onChange={e => setNewRequesterPhone(e.target.value)}
                    placeholder="e.g. +1 555 000 0000"
                    style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Department</label>
                  <select
                    value={newRequesterDept}
                    onChange={e => setNewRequesterDept(e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit', background: '#fff' }}
                  >
                    <option value="">-- Select --</option>
                    {(masterData.department || []).map(d => (
                      <option key={d.id} value={d.value}>{d.value}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Company</label>
                <input
                  value={newRequesterCompany}
                  onChange={e => setNewRequesterCompany(e.target.value)}
                  placeholder="e.g. Acme Corp"
                  style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Requester Group</label>
                <select
                  value={newRequesterGroupId}
                  onChange={e => setNewRequesterGroupId(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit', background: '#fff' }}
                >
                  <option value="">-- No Group --</option>
                  {requesterGroups.map(g => (
                    <option key={g.id} value={String(g.id)}>{g.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>PowerDesk password (not Microsoft 365)</label>
                <input
                  type="password"
                  placeholder="Leave blank to auto-generate"
                  value={newRequesterPassword}
                  onChange={e => setNewRequesterPassword(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
                />
                <div style={{ fontSize: 12, color: T.muted, marginTop: 6, lineHeight: 1.45 }}>
                  For the ticket portal only. Does not change Outlook/Teams/Azure — use Password Reset Assistant for M365.
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24, borderTop: `1px solid ${T.border}`, paddingTop: 16 }}>
              <button onClick={() => setShowAddRequesterModal(false)} style={{ padding: '9px 20px', border: `1px solid ${T.border}`, borderRadius: 8, background: '#FFFFFF', color: '#2D3748', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button
                onClick={handleAddRequester}
                disabled={!newRequesterName.trim() || !newRequesterEmail.trim()}
                style={{ padding: '9px 20px', border: 'none', borderRadius: 8, background: (newRequesterName.trim() && newRequesterEmail.trim()) ? '#2563eb' : '#C8CDD6', color: '#fff', fontWeight: 700, cursor: (newRequesterName.trim() && newRequesterEmail.trim()) ? 'pointer' : 'not-allowed', fontSize: 13 }}
              >
                Add Requester
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddUserModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#FFFFFF', borderRadius: 16, padding: 28, width: 460, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#1A202C' }}>Add User</div>
              <button onClick={() => setShowAddUserModal(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}>
                <Icon name="close" size={18} color={T.muted} />
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Full Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  value={newUserName}
                  onChange={e => setNewUserName(e.target.value)}
                  placeholder="e.g. John Smith"
                  style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Email <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  value={newUserEmail}
                  onChange={e => setNewUserEmail(e.target.value)}
                  placeholder="e.g. john.smith@company.com"
                  type="email"
                  style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Role</label>
                <select
                  value={newUserRole}
                  onChange={e => setNewUserRole(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit', background: '#FFFFFF', color: newUserRole ? '#1A202C' : '#718096' }}
                >
                  <option value="">Select a role...</option>
                  <option>Agent</option>
                  <option>Senior Agent</option>
                  <option>Team Lead</option>
                  <option>Supervisor</option>
                  <option>Manager</option>
                  <option>IT Admin</option>
                  <option>Support Engineer</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>PowerDesk password (not Microsoft 365)</label>
                <input
                  type="password"
                  placeholder="Leave blank to auto-generate"
                  value={newUserPassword}
                  onChange={e => setNewUserPassword(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
                />
                <div style={{ fontSize: 12, color: T.muted, marginTop: 6, lineHeight: 1.45 }}>
                  Agent/admin sign-in for PowerDesk only. O365 password is reset separately via the assistant after sign-in (lock icon).
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24, borderTop: `1px solid ${T.border}`, paddingTop: 16 }}>
              <button onClick={() => setShowAddUserModal(false)} style={{ padding: '9px 20px', border: `1px solid ${T.border}`, borderRadius: 8, background: '#FFFFFF', color: '#2D3748', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button
                onClick={handleAddUser}
                disabled={!newUserName.trim() || !newUserEmail.trim()}
                style={{ padding: '9px 20px', border: 'none', borderRadius: 8, background: (newUserName.trim() && newUserEmail.trim()) ? T.active : '#C8CDD6', color: '#fff', fontWeight: 700, cursor: (newUserName.trim() && newUserEmail.trim()) ? 'pointer' : 'not-allowed', fontSize: 13 }}
              >
                Add User
              </button>
            </div>
          </div>
        </div>
      )}

      {editingGroup && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#FFFFFF', borderRadius: 16, padding: 28, width: 520, maxWidth: '95vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#1A202C' }}>Edit Agent Group</div>
              <button onClick={() => setEditingGroup(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}>
                <Icon name="close" size={18} color={T.muted} />
              </button>
            </div>

            <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Group Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  value={editGroupName}
                  onChange={e => setEditGroupName(e.target.value)}
                  placeholder="e.g. Customer Success Team"
                  style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
                />
              </div>

              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Description</label>
                <textarea
                  value={editGroupDesc}
                  onChange={e => setEditGroupDesc(e.target.value)}
                  placeholder="Brief description of this group..."
                  rows={2}
                  style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, resize: 'vertical', outline: 'none', fontFamily: 'inherit' }}
                />
              </div>

              <div style={{ position: 'relative' }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: 6 }}>Agents</label>
                <div
                  onClick={() => setEditAgentDropdownOpen(o => !o)}
                  style={{ border: `1px solid ${editAgentDropdownOpen ? T.active : T.border}`, borderRadius: 8, padding: '8px 10px', cursor: 'pointer', display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', minHeight: 42, background: '#FFFFFF', boxSizing: 'border-box' }}
                >
                  {editGroupAgents.length === 0 && <span style={{ color: '#718096', fontSize: 13 }}>Select agents...</span>}
                  {editGroupAgents.map(agent => (
                    <span key={agent} style={{ background: '#EBF0FF', color: T.active, border: `1px solid #C7D2FE`, borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {agent.split(' ')[0]}
                      <button
                        type="button"
                        onMouseDown={e => { e.stopPropagation(); setEditGroupAgents(prev => prev.filter(a => a !== agent)); }}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: T.active, padding: 0, fontSize: 14, lineHeight: 1, display: 'flex', alignItems: 'center' }}
                      >�</button>
                    </span>
                  ))}
                  <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <Icon name={editAgentDropdownOpen ? 'chevronU' : 'chevronD'} size={13} color={T.muted} />
                  </span>
                </div>
                {editAgentDropdownOpen && (
                  <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 1001 }} onClick={() => setEditAgentDropdownOpen(false)} />
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 1002, background: '#FFFFFF', border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: 240 }}>
                      <div style={{ padding: '8px 10px', borderBottom: `1px solid ${T.border}` }}>
                        <input
                          autoFocus
                          value={editAgentSearch}
                          onChange={e => setEditAgentSearch(e.target.value)}
                          placeholder="Search agents..."
                          onClick={e => e.stopPropagation()}
                          style={{ width: '100%', border: 'none', outline: 'none', fontSize: 13, fontFamily: 'inherit', background: 'transparent' }}
                        />
                      </div>
                      <div style={{ overflowY: 'auto', flex: 1 }}>
                        {allAgents.filter(a => a.toLowerCase().includes(editAgentSearch.toLowerCase())).map(agent => {
                          const initials = agent.split(' ').map(n => n[0]).join('').slice(0, 2);
                          const checked = editGroupAgents.includes(agent);
                          return (
                            <label key={agent} onMouseDown={e => e.preventDefault()} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer', background: checked ? '#EBF0FF' : '#fff', borderBottom: `1px solid ${T.border}` }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => setEditGroupAgents(prev => checked ? prev.filter(a => a !== agent) : [...prev, agent])}
                                style={{ accentColor: T.active, width: 15, height: 15, flexShrink: 0 }}
                              />
                              <div style={{ width: 28, height: 28, borderRadius: 7, background: T.g50, display: 'grid', placeItems: 'center', color: T.active, fontWeight: 800, fontSize: 11, flexShrink: 0 }}>{initials}</div>
                              <span style={{ fontSize: 13, fontWeight: 600, color: '#1A202C' }}>{agent}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
                {editGroupAgents.length > 0 && <div style={{ fontSize: 12, color: T.active, marginTop: 5, fontWeight: 600 }}>{editGroupAgents.length} agent{editGroupAgents.length !== 1 ? 's' : ''} selected</div>}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, borderTop: `1px solid ${T.border}`, paddingTop: 16 }}>
              <button onClick={() => setEditingGroup(null)} style={{ padding: '9px 20px', border: `1px solid ${T.border}`, borderRadius: 8, background: '#FFFFFF', color: '#2D3748', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button
                onClick={handleSaveEditGroup}
                disabled={!editGroupName.trim()}
                style={{ padding: '9px 20px', border: 'none', borderRadius: 8, background: editGroupName.trim() ? T.active : '#C8CDD6', color: '#fff', fontWeight: 700, cursor: editGroupName.trim() ? 'pointer' : 'not-allowed', fontSize: 13 }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
